<template>
  <section class="swap-marketplace">
    <div v-if="feedback" :class="['swap-marketplace__feedback', feedbackType === 'error' ? 'is-error' : 'is-success']">{{ feedback }}</div>

    <section v-if="!cart && !order" class="container cont checkout-step checkout-step--clean swap-checkout-step">
      <div class="checkout-step__intro">
        <small class="form-text text-muted checkout-step__eyebrow">Paso 1 de 4</small>
        <h2 class="va-h6 checkout-step__title"><strong>Selecciona tus entradas</strong></h2>
        <p class="checkout-step__hint"><strong><i class="fas fa-exchange-alt" aria-hidden="true"></i> FOMO SWAP:</strong> Compra entradas de asistentes que están vendiendo o intercambiando sus entradas. FOMOPLANES protege tu compra al 100%.</p>
      </div>

      <div v-if="loading && !listings.length && !officialTickets.length" class="checkout-step__selection-layout">
        <div class="checkout-step__selection-main">
          <div class="swap-marketplace__empty">Buscando localidades...</div>
        </div>
      </div>

      <div v-else-if="!localityGroups.length" class="checkout-step__selection-layout">
        <div class="checkout-step__selection-main">
          <div class="swap-marketplace__empty">Aún no hay localidades configuradas para este FOMO Swap.</div>
        </div>
      </div>

      <div v-else class="checkout-step__selection-layout">
        <div class="checkout-step__selection-main">
          <div v-if="buyerStage === 'localities'" class="checkout-ticket-grid">
            <article v-for="group in localityGroups" :key="group.key" :class="['checkout-ticket-card', { 'is-selected': selectedLocalityKey === group.key }]" @click="selectLocality(group)">
              <div class="checkout-ticket-card__media">
                <div class="checkout-ticket-card__placeholder">
                  <i :class="group.isNumbered ? 'fas fa-chair' : 'fas fa-ticket-alt'"></i>
                </div>
              </div>

              <div class="checkout-ticket-card__body">
                <div class="checkout-ticket-card__top">
                  <div>
                    <span v-if="selectedLocalityKey === group.key" class="checkout-ticket-card__selected">Seleccionado</span>
                    <h3 class="checkout-ticket-card__title">{{ group.name }}</h3>
                    <p class="checkout-ticket-card__description">{{ localityCardCopy(group) }}</p>
                  </div>
                  <div class="checkout-ticket-card__price">
                    <span>{{ group.fromPriceLabel }}</span>
                  </div>
                </div>

                <div class="checkout-ticket-card__bottom">
                  <div class="quantity-toggle checkout-ticket-card__quantity" @click.stop>
                    <button type="button" :disabled="groupQuantity(group) <= 0" @click="decrementLocality(group)">&minus;</button>
                    <input type="text" :value="groupQuantity(group)" readonly>
                    <button type="button" :disabled="groupQuantity(group) >= group.maxQuantity" @click="incrementLocality(group)">+</button>
                  </div>

                  <label v-if="selectedLocalityKey === group.key && selectedLocalityShowsSeatsTogether" class="swap-marketplace__split-check" @click.stop>
                    <input v-model="seatsTogetherOnly" type="checkbox" />
                    <span>Quiero asientos juntos</span>
                  </label>

                  <div class="checkout-ticket-card__total">{{ localityCardFooter(group) }}</div>
                </div>
              </div>
            </article>
          </div>

          <div v-else class="swap-marketplace__results-panel">
            <div class="swap-marketplace__results-head">
              <div>
                <span>Entradas disponibles</span>
                <h4>{{ selectedSearchTitle }}</h4>
                <p>{{ selectedSearchCopy }}</p>
              </div>
              <button class="swap-marketplace__refresh" type="button" @click="backToLocalities">Cambiar localidad</button>
            </div>
            <p v-if="selectedLocalityAvailabilityMessage" class="swap-marketplace__availability-note">{{ selectedLocalityAvailabilityMessage }}</p>

            <div v-if="!matchingListings.length" class="swap-marketplace__empty swap-marketplace__empty--notice">
              <strong class="swap-marketplace__empty-title">Sin entradas publicadas ahora</strong>
              <p>Esta localidad no tiene vendedores activos en este momento. Prueba otra localidad, baja la cantidad o vuelve más tarde; FOMO solo muestra entradas disponibles y protegidas.</p>
            </div>

            <div v-else class="checkout-ticket-grid">
              <article v-for="listing in matchingListings" :key="listing.id" :class="['checkout-ticket-card', { 'is-selected': Number(selectedQuantities[listing.id] || 0) > 0 }]" @click="selectResultListing(listing)">
                <div class="checkout-ticket-card__media">
                  <div class="checkout-ticket-card__placeholder">
                    <i :class="listing.seats_together ? 'fas fa-chair' : 'fas fa-ticket-alt'"></i>
                  </div>
                </div>

                <div class="checkout-ticket-card__body">
                  <div class="checkout-ticket-card__top">
                    <div>
                      <span v-if="Number(selectedQuantities[listing.id] || 0) > 0" class="checkout-ticket-card__selected">Seleccionado x{{ selectedQuantities[listing.id] }}</span>
                      <h3 class="checkout-ticket-card__title">{{ listing.title || (selectedLocality && selectedLocality.name) || 'Entrada FOMO Swap' }}</h3>
                      <p class="checkout-ticket-card__description">{{ listingCardCopy(listing) }}</p>
                    </div>
                    <div class="checkout-ticket-card__price">
                      <span>{{ resultPriceLabel(listing) }}</span>
                      <small>{{ resultAvailabilityLabel(listing) }}</small>
                    </div>
                  </div>

                  <div class="checkout-ticket-card__bottom">
                    <div class="quantity-toggle checkout-ticket-card__quantity" @click.stop>
                      <button type="button" :disabled="resultSelectedQuantity(listing) <= 0" @click="decrementResultListing(listing)">&minus;</button>
                      <input type="text" :value="resultSelectedQuantity(listing)" readonly>
                      <button type="button" :disabled="!canIncrementResultListing(listing)" @click="incrementResultListing(listing)">+</button>
                    </div>

                    <div class="swap-marketplace__meta">
                      <span><i class="fas fa-chair"></i>{{ seatLabel(listing) }}</span>
                      <span><i class="fas fa-truck"></i>{{ deliveryLabel(listing.delivery_method) }}</span>
                    </div>
                    <div class="checkout-ticket-card__total">{{ listing.seats_together ? 'Asientos juntos' : 'Asientos separados o por coordinar' }}</div>
                  </div>

                  <div v-if="listing.delivery_instruction" class="swap-marketplace__instruction-card">
                    <strong>{{ listing.delivery_instruction.label }}</strong>
                    <span>{{ listing.delivery_instruction.short }}</span>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </div>

        <aside :class="['checkout-step__selection-summary', { 'is-empty': swapSummaryDisabled }]" aria-live="polite">
          <span class="checkout-step__selection-kicker">Resumen</span>
          <div class="checkout-step__selection-head">
            <div class="checkout-step__selection-copy">
              <strong>{{ swapSummaryHeadline }}</strong>
              <p>{{ swapSummaryCopy }}</p>
            </div>
            <span class="checkout-step__selection-total checkout-step__selection-total--inline">{{ swapSummaryTotalLabel }}</span>
          </div>

          <div class="checkout-step__selection-meta">
            <span>{{ swapSummaryQuantityMeta }}</span>
            <span>{{ selectedLocality ? selectedLocality.name : 'Sin localidad' }}</span>
            <span>{{ swapSummaryMeta }}</span>
          </div>

          <div v-if="selectedItems.length" class="checkout-step__selection-list">
            <article v-for="item in selectedItems" :key="item.listing.id" class="checkout-step__selection-item">
              <div>
                <strong>{{ item.listing.title || selectedLocality.name }}</strong>
                <small>x{{ item.quantity }}</small>
              </div>
              <span>{{ money((Number.parseFloat(item.listing.min_total || item.listing.unit_price || 0) || 0) * item.quantity, item.listing.currency || 'USD') }}</span>
            </article>
          </div>
          <div v-else class="checkout-step__selection-empty">Tu resumen aparecerá aquí cuando elijas localidad y entrada.</div>

          <label v-if="requiresSplitAcceptance" class="swap-marketplace__split-check swap-marketplace__split-check--summary">
            <input v-model="splitAccepted" type="checkbox" />
            <span>Acepto que los tickets pueden no estar juntos.</span>
          </label>

          <div class="checkout-step__selection-footer">
            <span class="checkout-step__selection-total checkout-step__selection-total--footer">{{ swapSummaryTotalLabel }}</span>
            <button @click="continueBuyerFlow" :disabled="swapSummaryDisabled || quoting || (requiresSplitAcceptance && !splitAccepted)" class="btn btn-primary checkout-step__button checkout-step__button--summary liquid-page-button" type="button">
              <span class="liquid-page-button__glow"></span>
              <span class="liquid-page-button__ring"></span>
              <span class="liquid-page-button__inner">
                <span class="liquid-page-button__copy">{{ quoting ? 'Reservando...' : swapSummaryCta }}</span>
                <span class="liquid-page-button__icon"><i class="fas fa-arrow-right"></i></span>
              </span>
            </button>
          </div>
        </aside>
      </div>

      <Teleport to="body">
        <div class="checkout-step__mobile-dock" aria-live="polite">
          <div :class="['checkout-step__mobile-card', { 'is-empty': swapSummaryDisabled }]">
            <div class="checkout-step__mobile-copy">
              <span class="checkout-step__mobile-total">{{ swapSummaryTotalLabel }}</span>
              <strong>{{ swapMobileHeadline }}</strong>
              <small>{{ swapMobileSummaryLine }}</small>
            </div>
            <button @click="continueBuyerFlow" :disabled="swapSummaryDisabled || quoting || (requiresSplitAcceptance && !splitAccepted)" class="btn btn-primary checkout-step__button checkout-step__button--mobile liquid-page-button" type="button">
              <span class="liquid-page-button__glow"></span>
              <span class="liquid-page-button__ring"></span>
              <span class="liquid-page-button__inner">
                <span class="liquid-page-button__copy">{{ swapMobileCta }}</span>
                <span class="liquid-page-button__icon"><i class="fas fa-arrow-right"></i></span>
              </span>
            </button>
          </div>
        </div>
      </Teleport>
    </section>

    <section v-if="cart && !order && cartStage === 'details'" class="container cont checkout-step checkout-step--clean swap-checkout-step swap-checkout-step--details">
      <form class="checkout-step__form checkout-step__form--wide swap-details-form" method="post" @submit.prevent="continueToPayment">
        <div class="checkout-step__intro swap-step-intro">
          <small class="form-text text-muted checkout-step__eyebrow">Paso 2 de 4</small>
          <h2 class="text-justify va-h6 checkout-step__title"><strong>Protege tu compra</strong></h2>
          <p class="checkout-step__hint">Confirma el correo donde verás esta orden en Mis tickets. FOMO usa estos datos para validar pago, entrega y soporte.</p>
        </div>

        <div class="swap-details-guard" aria-label="Resumen de protección FOMO Swap">
          <article>
            <span><i class="fas fa-clock" aria-hidden="true"></i> Reserva temporal</span>
            <strong>{{ cartHoldCopy }}</strong>
          </article>
          <article>
            <span><i class="fas fa-shield-alt" aria-hidden="true"></i> Compra protegida</span>
            <strong>FOMOPLANES verifica pago y entrega.</strong>
          </article>
          <article>
            <span><i class="fas fa-ticket-alt" aria-hidden="true"></i> Gestión</span>
            <strong>Todo continúa en Mis tickets.</strong>
          </article>
        </div>

        <div class="checkout-form-grid">
          <div class="checkout-field checkout-field--full">
            <label class="checkout-label" for="swap-buyer-email">Correo de compra</label>
            <input id="swap-buyer-email" class="form-control checkout-input" v-model.trim="checkoutForm.customer_email" type="email" placeholder="tu@email.com" required @input="lookupCheckoutCustomer" />
            <small v-if="customerLookupLoading" class="swap-marketplace__field-hint">Validando si este correo ya tiene cuenta FOMO...</small>
            <small v-else-if="userSearched && !requireAccount" class="swap-marketplace__field-hint is-success">Cuenta encontrada. Solo confirma el nombre que aparecerá en la orden.</small>
            <small v-else-if="userSearched && requireAccount" class="swap-marketplace__field-hint is-warning">No encontramos cuenta con este correo. Crea una contraseña para entrar luego a Mis tickets.</small>
            <small v-else class="swap-marketplace__field-hint">Usaremos este correo para confirmación, comprobantes y seguimiento de la entrega.</small>
          </div>

          <template v-if="requireAccount">
            <div class="checkout-field">
              <label class="checkout-label" for="swap-buyer-phone">Teléfono de contacto</label>
              <input id="swap-buyer-phone" class="form-control checkout-input" v-model.trim="checkoutForm.customer_phone" type="tel" placeholder="0999999999" required />
            </div>
            <div class="checkout-field">
              <label class="checkout-label" for="swap-buyer-address">Dirección de facturación</label>
              <input id="swap-buyer-address" class="form-control checkout-input" v-model.trim="checkoutForm.customer_address" type="text" placeholder="Dirección de domicilio" required />
            </div>
            <div class="checkout-field checkout-field--full">
              <label class="checkout-label" for="swap-buyer-password">Contraseña de acceso</label>
              <input id="swap-buyer-password" class="form-control checkout-input" v-model="checkoutForm.customer_password" type="password" placeholder="Crea una contraseña para revisar tus tickets rápido" required />
            </div>
          </template>
        </div>

        <div v-if="requireAccount" class="checkout-check">
          <div
            class="checkout-check__button"
            :class="{ 'is-checked': acceptedTerms }"
            role="checkbox"
            tabindex="0"
            :aria-checked="acceptedTerms ? 'true' : 'false'"
            @click="toggleAcceptedTerms"
            @keydown.enter.prevent="toggleAcceptedTerms"
            @keydown.space.prevent="toggleAcceptedTerms"
          >
            <span class="checkout-check__box" aria-hidden="true"></span>
            <span class="checkout-check__text">Al continuar aceptas los <span class="checkout-check__link" role="button" tabindex="0" @click.stop="openLegalModal('terms')" @keydown.enter.prevent.stop="openLegalModal('terms')" @keydown.space.prevent.stop="openLegalModal('terms')">términos y condiciones</span>.</span>
          </div>
          <small v-if="termsError" class="checkout-check__error">Debes aceptar los términos y condiciones para continuar.</small>
        </div>

        <TermsModal v-model="showLegalModal" :view="legalModalView" @update:view="legalModalView = $event" />

        <div v-if="userSearched" class="checkout-step__section">
          <h2 class="text-justify checkout-step__section-title"><strong>Nombre para la orden</strong></h2>
          <p class="checkout-step__section-text">Este nombre identifica al comprador dentro de FOMO Swap. La entrega y cualquier reclamo se gestionan desde Mis tickets.</p>

          <div class="checkout-guest-grid">
            <article class="checkout-guest-card" style="animation-delay: 0ms;">
              <div class="checkout-guest-card__header">
                <span class="checkout-guest-card__eyebrow">Comprador principal</span>
                <strong class="checkout-guest-card__title">{{ cart.event_name || event.name }}</strong>
                <span class="checkout-guest-card__meta">{{ reservedTicketCopy(cart.requested_quantity || selectedCount) }}</span>
              </div>

              <div class="checkout-form-grid checkout-form-grid--compact">
                <div class="checkout-field">
                  <label class="checkout-label">Nombres</label>
                  <input class="form-control checkout-input" v-model.trim="checkoutForm.customer_first_name" type="text" placeholder="Nombre del comprador" required />
                </div>
                <div class="checkout-field">
                  <label class="checkout-label">Apellidos</label>
                  <input class="form-control checkout-input" v-model.trim="checkoutForm.customer_last_name" type="text" placeholder="Apellido del comprador" required />
                </div>
              </div>
            </article>
          </div>

          <p v-if="checkoutError" class="checkout-check__error checkout-step__field-error">{{ checkoutError }}</p>

          <div class="checkout-step__actions checkout-step__actions--dock">
            <div class="checkout-step__actions-copy">
              <span class="checkout-step__summary-label">Datos listos</span>
              <small class="checkout-step__summary-note">Continúa a pago para cerrar la compra protegida.</small>
            </div>
            <button class="btn btn-primary btn-block checkout-step__button liquid-page-button" type="submit" :disabled="!canContinueToPayment || customerLookupLoading || creatingCustomer">
              <span class="liquid-page-button__glow"></span>
              <span class="liquid-page-button__ring"></span>
              <span class="liquid-page-button__inner">
                <span class="liquid-page-button__copy">{{ creatingCustomer ? 'Creando cuenta...' : 'Continuar' }}</span>
              </span>
            </button>
          </div>
        </div>

        <div class="swap-marketplace__cart-actions swap-marketplace__cart-actions--normal">
          <button type="button" :disabled="releasing" @click="releaseCart">{{ releasing ? 'Liberando...' : 'Liberar reserva' }}</button>
        </div>
      </form>
    </section>

    <section v-if="cart && !order && cartStage === 'payment'" class="container cont checkout-step checkout-step--payment checkout-step--clean swap-checkout-step">
      <div class="checkout-step__intro checkout-step__intro--with-total">
        <div>
          <small class="form-text text-muted checkout-step__eyebrow">Paso 3 de 4</small>
          <h2 class="text-justify va-h6 checkout-step__title"><strong>Pago</strong></h2>
          <p class="checkout-step__hint">Elige tu método de pago y confirma la compra en una sola columna.</p>
        </div>
        <div class="checkout-step__intro-total">{{ money(cart.totals.customer_total, cart.totals.currency) }}</div>
      </div>

      <div class="row rw checkout-step__scene checkout-step__scene--payment" style="text-align:center;">
        <div class="checkout-payment-summary">
          <div class="checkout-payment-summary__item">
            <span>Subtotal</span>
            <strong>{{ money(cartSubtotal, cartCurrency) }}</strong>
          </div>
          <div class="checkout-payment-summary__item">
            <span>Fee FOMOPLANES</span>
            <strong>{{ money(cartBuyerFee, cartCurrency) }}</strong>
          </div>
          <div class="checkout-payment-summary__item checkout-payment-summary__item--total">
            <span>Total</span>
            <strong>{{ money(cartCustomerTotal, cartCurrency) }}</strong>
          </div>
        </div>

        <div class="checkout-step__payment-head">
          <p class="checkout-step__helper checkout-step__helper--lead" style="padding-bottom: 10px;">Selecciona tu forma de pago preferida.</p>
          <div class="checkout-step__trust">
            <span>Checkout seguro</span>
            <span>Protección 100% FOMOPLANES</span>
            <span>Sin fricción</span>
          </div>
        </div>

        <div v-if="checkoutError" class="workspace-feedback workspace-feedback--error">{{ checkoutError }}</div>

        <div v-if="!availableEventPaymentOptions.length" class="swap-marketplace__empty">No hay métodos de pago habilitados para este evento.</div>

        <component
          v-if="activePaymentRail === 'bank_transfer'"
          :is="bankPaymentComponent"
          :event="event.id"
          :newTotal="paymentOptionTotal()"
          :total="paymentOptionTotal()"
          :subtotal="cartSubtotal.toFixed(2)"
          :currency="cartCurrency"
          @returnMessageFromBankAccount="returnFromBankPayment"
          @messageFromBankAccount="handleBankProof"
        />

        <div v-else class="swap-marketplace__payment-stack">
          <div v-for="option in paypalPaymentOptions" :key="`paypal-${option.id}`" class="form-group fmbt checkout-method-card checkout-payment-embed checkout-payment-embed--paypal swap-marketplace__paypal-card">
            <span class="checkout-payment-embed__label">{{ paymentOptionLabel(option, 'PayPal') }}</span>
            <p class="swap-marketplace__payment-copy">Paga con PayPal y FOMO crea la orden solo cuando PayPal confirma la transacción.</p>
            <VueExMXN v-if="isPayPalMxOption(option)" :cartTotal="paymentOptionTotal()" @messageFromPaypalMXN="(tranId) => handlePaypalMXNMessageForOption(option, tranId)" style="display: block; width: 100%;" />
            <VueEx v-else :cartTotal="paymentOptionTotal()" @messageFromPaypal="(tranId) => handlePaypalMessageForOption(option, tranId)" style="display: block; width: 100%;" />
          </div>

          <div v-if="selectablePaymentOptions.length" class="checkout-step__methods swap-marketplace__method-buttons" style="padding-bottom: 30px;">
            <div v-for="option in selectablePaymentOptions" :key="option.id" :class="['form-group fmbt checkout-method-card', { 'is-selected': Number(activePaymentOptionId) === Number(option.id) }]">
            <button @click="selectPaymentOption(option)" class="btn btn-primary btn-block btn-lg btn-bank checkout-step__button liquid-page-button" type="button" :disabled="checkingOut || preparingPayment" style="padding-bottom: 15px; margin-bottom: 15px;">
              <span class="liquid-page-button__glow"></span>
              <span class="liquid-page-button__ring"></span>
              <span class="liquid-page-button__inner">
                <span class="liquid-page-button__copy">{{ Number(activePaymentOptionId) === Number(option.id) && preparingPayment ? 'Preparando...' : paymentOptionLabel(option) }}</span>
                <span class="liquid-page-button__icon"><i :class="paymentOptionIcon(option)"></i></span>
              </span>
            </button>
            </div>
          </div>
        </div>

        <div v-if="activePaymentRail === 'pagoplux'" class="form-group fmbt checkout-method-card checkout-payment-embed">
          <span class="checkout-payment-embed__label">{{ paymentOptionLabel(activePaymentOption, 'Tarjeta') }}</span>
          <VuePagoPluxAR v-if="Number(activePaymentOption && activePaymentOption.id) === 7" :email="checkoutForm.customer_email" :pname="paymentOptionLabel(activePaymentOption, 'Tarjeta')" :address="checkoutForm.customer_address" :reload="false" :phone="checkoutForm.customer_phone" :total="paymentOptionTotal()" :event="event.id" :conversion_rate="1" @messageFromPagoPlux="handlePagoPluxArMessage" style="display: block; width: 100%;" />
          <VuePagoPlux v-else :email="checkoutForm.customer_email" :pname="paymentOptionLabel(activePaymentOption, 'Tarjeta')" :address="checkoutForm.customer_address" :reload="false" :phone="checkoutForm.customer_phone" :total="paymentOptionTotal()" :event="event.id" @messageFromPagoPlux="handlePagoPluxMessage" style="display: block; width: 100%;" />
        </div>

        <div v-if="activePaymentRail === 'stripe'" class="form-group fmbt checkout-method-card checkout-payment-embed checkout-payment-host checkout-payment-host--stripe">
          <span v-if="loadingStripeIntent || preparingPayment" class="checkout-payment-embed__label">Preparando formulario de tarjeta...</span>
          <StripePaymentElement v-if="showStripePaymentElement" :key="stripeIntent.clientSecret" :attempt-token="(pendingPaymentOrder && pendingPaymentOrder.order_number) || 'swap-stripe'" :client-secret="stripeIntent.clientSecret" :publishable-key="stripeIntent.publishableKey" :customer-name="checkoutFullName" :customer-email="checkoutForm.customer_email" :customer-phone="checkoutForm.customer_phone" :amount="stripeIntent.amount" :currency="stripeIntent.currency" @completed="handleStripeCompleted" />
        </div>

        <div class="checkout-step__legal-note">
          <p class="checkout-step__legal-copy">Al completar tu pago aceptas los <button type="button" class="checkout-step__legal-link" @click="openLegalModal('terms')">Términos y condiciones</button>, la <button type="button" class="checkout-step__legal-link" @click="openLegalModal('privacy')">Política de privacidad</button> y la <button type="button" class="checkout-step__legal-link" @click="openLegalModal('refunds')">Política de reembolsos</button> aplicables a esta compra.</p>
        </div>

        <TermsModal v-model="showLegalModal" :view="legalModalView" @update:view="legalModalView = $event" />

        <div class="swap-marketplace__cart-actions swap-marketplace__cart-actions--normal">
          <button type="button" :disabled="checkingOut" @click="cartStage = 'details'">Volver a datos</button>
          <button type="button" :disabled="releasing" @click="releaseCart">{{ releasing ? 'Liberando...' : 'Liberar reserva' }}</button>
        </div>
      </div>
    </section>

    <article v-if="order" class="swap-marketplace__cart swap-marketplace__cart--order swap-marketplace__order-created">
      <div>
        <span>Orden creada</span>
        <h4>{{ order.order_number }}</h4>
        <p>Tu compra Swap quedó registrada por {{ money(order.totals.customer_total, order.totals.currency) }}. La gestión de pago, entrega, mensajes y reclamos vive en Mis tickets.</p>
        <div class="swap-marketplace__order-status">
          <span>Pago {{ paymentStatusLabel(order.payment_status) }}</span>
          <span>Entrega {{ orderDeliverySummary(order) }}</span>
        </div>
        <div v-if="canPayStripeOrder(order)" class="swap-marketplace__stripe-box">
          <button v-if="!stripeIntent.clientSecret" class="swap-marketplace__hold-button" type="button" :disabled="loadingStripeIntent" @click="prepareStripePayment">
            {{ loadingStripeIntent ? 'Preparando tarjeta...' : 'Pagar con tarjeta' }}
          </button>
          <StripePaymentElement
            v-else
            :key="stripeIntent.clientSecret"
            :attempt-token="order.order_number"
            :client-secret="stripeIntent.clientSecret"
            :publishable-key="stripeIntent.publishableKey"
            :customer-name="orderCustomerName(order)"
            :customer-email="orderCustomerEmail(order)"
            :customer-phone="orderCustomerPhone(order)"
            default-country-code="EC"
            :amount="stripeIntent.amount"
            :currency="stripeIntent.currency"
            @completed="handleStripeCompleted"
          />
        </div>
      </div>
      <div class="swap-marketplace__cart-actions">
        <router-link class="swap-marketplace__account-link" :to="{ name: 'usertickets', query: { tab: 'swap' } }">Ver en Mis tickets</router-link>
        <button type="button" @click="startAnotherPurchase">Comprar otra entrada</button>
        <button type="button" @click="copyOrderNumber">Copiar orden</button>
      </div>
    </article>
  </section>
</template>

<script>
import { defineAsyncComponent } from 'vue'
import { apiService, apiServiceForm } from '../../common/api.services.js'
import TermsModal from '../TermsModal.vue'
import BankAccount from './BankAccount.vue'
import BankAccount0Ec from './BankAccount0Ec.vue'
import BankAccount1Ec from './BankAccount1Ec.vue'
import VueEx from './VueEx.vue'
import VueExMXN from './VueExMXN.vue'
import VuePagoPlux from './VuePagoPlux.vue'
import VuePagoPluxAR from './VuePagoPluxAR.vue'

export default {
  name: 'SwapMarketplace',
  components: {
    StripePaymentElement: defineAsyncComponent(() => import('./StripePaymentElement.vue')),
    TermsModal,
    BankAccount,
    BankAccount0Ec,
    BankAccount1Ec,
    VueEx,
    VueExMXN,
    VuePagoPlux,
    VuePagoPluxAR,
  },
  props: {
    event: {
      type: Object,
      required: true,
    },
  },
  emits: ['quote-held'],
  data() {
    return {
      loading: false,
      quoting: false,
      releasing: false,
      checkingOut: false,
      creatingCustomer: false,
      customerLookupLoading: false,
      loadingOrder: false,
      loadingStripeIntent: false,
      submittingDispute: false,
      loadingMessages: false,
      listings: [],
      officialTickets: [],
      selectedQuantities: {},
      buyerStage: 'localities',
      selectedLocalityKey: '',
      requestedQuantity: 1,
      seatsTogetherOnly: true,
      splitAccepted: false,
      cart: null,
      cartStage: 'details',
      order: null,
      pendingPaymentOrder: null,
      orderLookupNumber: '',
      buyerConversations: [],
      buyerMessageDrafts: {},
      buyerMessageError: '',
      deliveryArtifacts: [],
      artifactAccessEmail: '',
      artifactAccessError: '',
      loadingArtifacts: false,
      sendingMessageKey: '',
      stripeIntent: this.defaultStripeIntent(),
      activePaymentOptionId: null,
      preparingPayment: false,
      disputeForm: this.defaultDisputeForm(),
      checkoutForm: this.defaultCheckoutForm(),
      checkoutCustomer: null,
      userSearched: false,
      requireAccount: false,
      acceptedTerms: false,
      termsError: false,
      checkoutError: '',
      checkoutLookupTimer: null,
      showLegalModal: false,
      legalModalView: 'terms',
      feedback: '',
      feedbackType: 'success',
    }
  },
  computed: {
    eventSlug() {
      return this.event && (this.event.slug || this.event.id)
    },
    selectedItems() {
      return this.listings
        .map((listing) => ({ listing, quantity: Number(this.selectedQuantities[listing.id] || 0) }))
        .filter((item) => item.quantity > 0)
    },
    availableListings() {
      return this.listings.filter((listing) => Number(listing.available_units || 0) > 0)
    },
    localityGroups() {
      const groups = new Map()
      this.officialTickets.forEach((ticket) => {
        const key = `official:${ticket.id}`
        const officialPrice = Number.parseFloat(ticket.official_price || ticket.price || 0) || 0
        const officialQuantity = Math.max(1, Number(ticket.quantity || 1))
        const maxQuantity = this.ticketMaxQuantity(ticket, officialQuantity)
        groups.set(key, {
          key,
          name: ticket.name || 'Localidad por confirmar',
          copy: ticket.description || 'Localidad oficial FOMO Swap.',
          currency: 'USD',
          minPrice: officialPrice,
          officialQuantity,
          availableQuantity: 0,
          maxQuantity,
          isNumbered: false,
          listings: [],
          priceLabel: officialPrice ? `Oficial ${this.money(officialPrice, 'USD')}` : 'Precio oficial',
        })
      })
      this.availableListings.forEach((listing) => {
        const key = this.localityKey(listing)
        const price = Number.parseFloat(listing.min_total || listing.unit_price || 0) || 0
        const officialTicket = this.officialTicket(listing)
        const listingOfficialQuantity = Math.max(1, Number((officialTicket && officialTicket.quantity) || 1))
        const listingMaxQuantity = officialTicket ? this.ticketMaxQuantity(officialTicket, listingOfficialQuantity) : 0
        const existing = groups.get(key) || {
          key,
          name: this.localityName(listing),
          copy: this.localityCopy(listing),
          currency: listing.currency || 'USD',
          minPrice: price,
          officialQuantity: listingOfficialQuantity,
          availableQuantity: 0,
          maxQuantity: listingMaxQuantity,
          isNumbered: false,
          listings: [],
          priceLabel: '',
        }
        existing.minPrice = existing.minPrice ? Math.min(existing.minPrice, price) : price
        existing.availableQuantity += Number(listing.available_units || 0)
        if (listingMaxQuantity) {
          existing.maxQuantity = Math.max(existing.maxQuantity || 1, listingMaxQuantity)
        } else {
          existing.maxQuantity = Math.min(10, Math.max(existing.maxQuantity || 1, existing.officialQuantity || 1, existing.availableQuantity || 1))
        }
        existing.isNumbered = existing.isNumbered || this.listingIsNumbered(listing)
        existing.listings.push(listing)
        groups.set(key, existing)
      })
      return Array.from(groups.values())
        .map((group) => ({ ...group, fromPriceLabel: group.priceLabel || `Desde ${this.money(group.minPrice, group.currency)}` }))
        .sort((a, b) => a.minPrice - b.minPrice || a.name.localeCompare(b.name))
    },
    selectedLocality() {
      return this.localityGroups.find((group) => group.key === this.selectedLocalityKey) || null
    },
    selectedLocalityShowsSeatsTogether() {
      return Boolean(this.selectedLocality && this.selectedLocality.isNumbered && this.requestedQuantity > 1)
    },
    matchingListings() {
      if (!this.selectedLocality) return []
      const requested = Math.max(1, Number(this.requestedQuantity || 1))
      const wantsTogether = Boolean(this.selectedLocalityShowsSeatsTogether && this.seatsTogetherOnly)
      return this.selectedLocality.listings
        .filter((listing) => {
          const available = Number(listing.available_units || 0)
          return available > 0
        })
        .sort((a, b) => {
          const availableA = Number(a.available_units || 0)
          const availableB = Number(b.available_units || 0)
          const enoughA = availableA >= requested ? 1 : 0
          const enoughB = availableB >= requested ? 1 : 0
          const togetherA = a.seats_together ? 1 : 0
          const togetherB = b.seats_together ? 1 : 0
          if (wantsTogether && togetherA !== togetherB) return togetherB - togetherA
          if (enoughA !== enoughB) return enoughB - enoughA
          if (availableA !== availableB) return availableB - availableA
          return (Number.parseFloat(a.min_total || a.unit_price || 0) - Number.parseFloat(b.min_total || b.unit_price || 0)) || Number(a.id) - Number(b.id)
        })
    },
    selectedLocalityAvailableCount() {
      if (!this.selectedLocality) return 0
      return this.selectedLocality.listings.reduce((sum, listing) => sum + Number(listing.available_units || 0), 0)
    },
    selectedCount() {
      return this.selectedItems.reduce((sum, item) => sum + item.quantity, 0)
    },
    selectedCountLabel() {
      const count = this.selectedCount
      return `${this.ticketCountLabel(count)} ${count === 1 ? 'seleccionada' : 'seleccionadas'}`
    },
    requiresSplitAcceptance() {
      return this.selectedItems.length > 1 || this.selectedItems.some((item) => item.quantity > 1 && !item.listing.seats_together)
    },
    estimatedTotalLabel() {
      const total = this.selectedItems.reduce((sum, item) => sum + (Number.parseFloat(item.listing.min_total || item.listing.unit_price || 0) * item.quantity), 0)
      return this.money(total, 'USD')
    },
    messageCopy() {
      const summary = this.order && this.order.messaging_summary ? this.order.messaging_summary : {}
      return summary.copy || 'FOMO coordina la entrega sin compartir contacto directo.'
    },
    selectedSearchTitle() {
      if (!this.selectedLocality) return 'Entradas disponibles'
      return `${this.selectedLocality.name} · hasta ${this.ticketCountLabel(this.requestedQuantity)}`
    },
    selectedSearchCopy() {
      if (!this.selectedLocality) return 'Elige una localidad para ver resultados.'
      if (this.selectedLocalityShowsSeatsTogether && this.seatsTogetherOnly) return 'Priorizamos opciones juntas; si no alcanzan, igual verás alternativas disponibles.'
      return 'Selecciona una o varias entradas disponibles. Puedes continuar con menos de lo pedido.'
    },
    selectedLocalityAvailabilityMessage() {
      if (!this.selectedLocality || this.buyerStage !== 'results') return ''
      const requested = Number(this.requestedQuantity || 0)
      const available = this.selectedLocalityAvailableCount
      if (available <= 0) return ''
      if (available < requested) {
        return `Pediste hasta ${this.ticketCountLabel(requested)}. Ahora hay ${this.ticketCountLabel(available)} disponibles; puedes comprar las disponibles o cambiar cantidad.`
      }
      if (available > requested) {
        return `Hay ${this.ticketCountLabel(available)} disponibles. Puedes seleccionar hasta ${this.ticketCountLabel(requested)} en esta compra.`
      }
      return `Hay ${this.ticketCountLabel(available)} disponibles para esta búsqueda.`
    },
    swapSummaryDisabled() {
      if (this.buyerStage === 'localities') return !this.selectedLocality
      return !this.selectedItems.length
    },
    swapSummaryHeadline() {
      if (this.buyerStage === 'localities') {
        return this.selectedLocality ? `Hasta ${this.ticketCountLabel(this.requestedQuantity)} en ${this.selectedLocality.name}` : 'Sin localidad seleccionada'
      }
      return this.selectedItems.length ? `${this.selectedCountLabel}` : 'Elige una entrada afín'
    },
    swapSummaryCopy() {
      if (this.buyerStage === 'localities') {
        return this.selectedLocality ? 'Busca entradas disponibles que coincidan con esa localidad y cantidad.' : 'Selecciona una localidad para iniciar la búsqueda.'
      }
      if (this.selectedItems.length && this.selectedCount < Number(this.requestedQuantity || 0)) {
        return `Pediste hasta ${this.ticketCountLabel(this.requestedQuantity)}; puedes continuar con ${this.ticketCountLabel(this.selectedCount)} o sumar más si hay disponibles.`
      }
      return this.selectedItems.length ? 'Revisa la selección antes de reservar y pasar a datos.' : 'Selecciona una opción disponible para continuar.'
    },
    swapSummaryMeta() {
      if (this.buyerStage === 'localities') return 'Siguiente: disponibles'
      if (this.selectedItems.length && this.selectedCount < Number(this.requestedQuantity || 0)) {
        return `Seleccionaste ${this.ticketCountLabel(this.selectedCount)} de hasta ${this.ticketCountLabel(this.requestedQuantity)}`
      }
      return this.selectedItems.length ? 'Siguiente: datos del comprador' : 'Siguiente paso bloqueado'
    },
    swapSummaryQuantityMeta() {
      if (this.buyerStage === 'localities') return `Objetivo: hasta ${this.ticketCountLabel(this.requestedQuantity)}`
      return this.selectedItems.length ? `Seleccionado: ${this.ticketCountLabel(this.selectedCount)}` : `Puedes elegir hasta ${this.ticketCountLabel(this.requestedQuantity)}`
    },
    swapSummaryTotalLabel() {
      if (this.buyerStage === 'localities' && this.selectedLocality) {
        return this.money((Number.parseFloat(this.selectedLocality.minPrice || 0) || 0) * Number(this.requestedQuantity || 0), this.selectedLocality.currency || 'USD')
      }
      return this.selectedItems.length ? this.estimatedTotalLabel : this.money(0, 'USD')
    },
    swapSummaryCta() {
      if (this.buyerStage === 'localities') return 'Buscar entradas'
      return this.selectedItems.length ? `Continuar con ${this.ticketCountLabel(this.selectedCount)}` : 'Selecciona entradas'
    },
    swapMobileHeadline() {
      if (this.buyerStage === 'localities') return this.selectedLocality ? 'Búsqueda lista' : 'Elige localidad'
      return this.selectedItems.length ? 'Selección lista' : 'Elige entradas'
    },
    swapMobileSummaryLine() {
      if (this.buyerStage === 'localities') return this.selectedLocality ? `${this.selectedLocality.name} x${this.requestedQuantity}` : 'Selecciona una localidad para buscar.'
      if (this.selectedItems.length) return this.selectedItems.map((item) => `${item.listing.title || this.selectedLocality.name} x${item.quantity}`).join(' · ')
      if (this.selectedLocalityAvailableCount > 0) return `Hay ${this.ticketCountLabel(this.selectedLocalityAvailableCount)}; elige hasta ${this.ticketCountLabel(this.requestedQuantity)}.`
      return 'No hay entradas disponibles ahora.'
    },
    swapMobileCta() {
      if (this.buyerStage === 'localities') return 'Buscar'
      return this.selectedItems.length ? 'Continuar' : 'Selecciona'
    },
    eventPaymentOptions() {
      const options = Array.isArray(this.event && this.event.payment_options) ? this.event.payment_options : []
      return options.filter((option) => option && option.id)
    },
    availableEventPaymentOptions() {
      return this.eventPaymentOptions.filter((option) => this.paymentOptionCheckoutAvailable(option) && this.paymentOptionSupportsEventCurrency(option) && this.paymentOptionRail(option))
    },
    paypalPaymentOptions() {
      return this.availableEventPaymentOptions.filter((option) => this.paymentOptionRail(option) === 'paypal')
    },
    selectablePaymentOptions() {
      return this.availableEventPaymentOptions.filter((option) => this.paymentOptionRail(option) !== 'paypal')
    },
    activePaymentOption() {
      return this.availableEventPaymentOptions.find((option) => Number(option.id) === Number(this.activePaymentOptionId)) || null
    },
    activePaymentRail() {
      return this.paymentOptionRail(this.activePaymentOption)
    },
    bankPaymentComponent() {
      if (!this.activePaymentOption) return 'BankAccount'
      if (Number(this.activePaymentOption.id) === 4) return 'BankAccount1Ec'
      if (Number(this.activePaymentOption.id) === 8) return 'BankAccount0Ec'
      return 'BankAccount'
    },
    showStripePaymentElement() {
      return this.activePaymentRail === 'stripe' && this.stripeIntent.clientSecret
    },
    cartCurrency() {
      return (this.cart && this.cart.totals && this.cart.totals.currency) || 'USD'
    },
    cartSubtotal() {
      return Number.parseFloat((this.cart && this.cart.totals && this.cart.totals.ticket_subtotal) || 0) || 0
    },
    cartBuyerFee() {
      return Number.parseFloat((this.cart && this.cart.totals && this.cart.totals.buyer_fee_total) || 0) || 0
    },
    cartCustomerTotal() {
      return Number.parseFloat((this.cart && this.cart.totals && this.cart.totals.customer_total) || 0) || 0
    },
    checkoutFullName() {
      return [this.checkoutForm.customer_first_name, this.checkoutForm.customer_last_name].map((value) => String(value || '').trim()).filter(Boolean).join(' ')
    },
    canContinueToPayment() {
      const emailReady = /\S+@\S+\.\S+/.test(this.checkoutForm.customer_email || '')
      const nameReady = Boolean(String(this.checkoutForm.customer_first_name || '').trim() && String(this.checkoutForm.customer_last_name || '').trim())
      if (!emailReady || !this.userSearched || !nameReady) return false
      if (!this.requireAccount) return true
      return Boolean(
        String(this.checkoutForm.customer_phone || '').trim()
        && String(this.checkoutForm.customer_address || '').trim()
        && String(this.checkoutForm.customer_password || '').trim()
        && this.acceptedTerms
      )
    },
    cartHoldCopy() {
      const expiresAt = this.cart && this.cart.expires_at ? new Date(this.cart.expires_at) : null
      if (expiresAt && !Number.isNaN(expiresAt.getTime())) {
        return `Reserva vence a las ${expiresAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
      }
      return 'Apartada por 10 minutos.'
    },
  },
  created() {
    this.loadListings()
  },
  methods: {
    defaultCheckoutForm() {
      return {
        customer_first_name: '',
        customer_last_name: '',
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        customer_address: '',
        customer_password: '',
        payment_option: null,
        payment_rail: '',
        provider_reference: '',
      }
    },
    defaultStripeIntent() {
      return { paymentIntentId: '', clientSecret: '', publishableKey: '', amount: 0, currency: 'USD' }
    },
    defaultDisputeForm() {
      return { customer_email: '', order_item: '', reason: 'invalid_ticket', description: '' }
    },
    orderStorageKey() {
      return `fomo_swap_order_${this.eventSlug || 'event'}`
    },
    money(value, currency = 'USD') {
      const amount = Number(value || 0)
      try { return new Intl.NumberFormat('es-EC', { style: 'currency', currency: currency || 'USD' }).format(amount) } catch (_error) { return `${amount.toFixed(2)} ${currency || 'USD'}` }
    },
    extractError(payload, fallback) {
      if (!payload) return fallback
      if (payload.err) return payload.err
      if (payload.detail) return Array.isArray(payload.detail) ? payload.detail.join(', ') : payload.detail
      const firstValue = typeof payload === 'object' ? Object.values(payload)[0] : null
      if (Array.isArray(firstValue)) return firstValue[0]
      return firstValue || fallback
    },
    normalizeCurrency(value, fallback = 'USD') {
      const normalized = String(value || fallback || 'USD').trim().toUpperCase()
      return normalized || fallback
    },
    eventPresentmentCurrency() {
      return this.normalizeCurrency(this.event && this.event.presentment_currency)
    },
    paymentOptionCurrency(option) {
      const profile = option && option.pricing_profile ? option.pricing_profile : {}
      return this.normalizeCurrency(profile.currency || (this.event && this.event.presentment_currency) || 'USD')
    },
    paymentOptionCheckoutAvailable(option) {
      return Boolean(option) && option.checkout_available !== false && !option.checkout_restriction_code
    },
    paymentOptionSupportsEventCurrency(option) {
      const eventCurrency = this.eventPresentmentCurrency()
      if (this.isPayPalMxOption(option)) return eventCurrency === 'MXN'
      if (this.isPayPalOption(option)) return eventCurrency === 'USD'
      if (this.isRebillOption(option)) return this.paymentOptionCurrency(option) === eventCurrency
      return true
    },
    paymentOptionRawLabel(option) {
      if (!option || typeof option !== 'object') return ''
      const profileLabel = option.pricing_profile && option.pricing_profile.label ? String(option.pricing_profile.label).trim() : ''
      const optionName = option.name ? String(option.name).trim() : ''
      return profileLabel || optionName
    },
    paymentOptionLabel(option, fallback = '') {
      const rawLabel = this.paymentOptionRawLabel(option)
      const normalizedLabel = `${rawLabel} ${option && option.name ? option.name : ''} ${option && option.code ? option.code : ''}`.toLowerCase()
      if (normalizedLabel.includes('stripe') || normalizedLabel.includes('rebill')) return 'Tarjeta de crédito o débito'
      if (/^tmp\b/i.test(rawLabel)) return this.isPayPalMxOption(option) ? 'PayPal MXN' : 'PayPal'
      if (normalizedLabel.includes('paypal')) return this.isPayPalMxOption(option) ? 'PayPal MXN' : 'PayPal'
      return rawLabel || fallback || 'Metodo de pago'
    },
    isStripeOption(option) {
      const haystack = `${option && option.name ? option.name : ''} ${option && option.code ? option.code : ''}`.toLowerCase()
      return haystack.includes('stripe')
    },
    isRebillOption(option) {
      const haystack = `${option && option.name ? option.name : ''} ${option && option.code ? option.code : ''}`.toLowerCase()
      return haystack.includes('rebill')
    },
    isPayPalOption(option) {
      const label = this.paymentOptionRawLabel(option).toLowerCase()
      const code = String((option && option.code) || '').toLowerCase()
      return label.includes('paypal') || code.includes('paypal') || /^tmp\b/i.test(label)
    },
    isPayPalMxOption(option) {
      const label = `${this.paymentOptionRawLabel(option)} ${(option && option.code) || ''}`.toLowerCase()
      return this.isPayPalOption(option) && /mxn|mx|mex/.test(label)
    },
    isPagoPluxOption(option) {
      const haystack = `${option && option.name ? option.name : ''} ${option && option.code ? option.code : ''}`.toLowerCase()
      return [3, 5, 7].includes(Number(option && option.id)) || haystack.includes('pagoplux')
    },
    isBankTransferOption(option) {
      const haystack = `${option && option.name ? option.name : ''} ${option && option.code ? option.code : ''}`.toLowerCase()
      return [2, 4, 8].includes(Number(option && option.id)) || haystack.includes('bank') || haystack.includes('transfer')
    },
    paymentOptionRail(option) {
      if (!option) return ''
      const haystack = `${option.name || ''} ${option.code || ''}`.toLowerCase()
      if (this.isStripeOption(option)) return 'stripe'
      if (this.isRebillOption(option)) return 'rebill'
      if (this.isPayPalOption(option)) return 'paypal'
      if (this.isBankTransferOption(option)) return 'bank_transfer'
      if (this.isPagoPluxOption(option)) return 'pagoplux'
      if (haystack.includes('mercado')) return 'mercadopago'
      if (haystack.includes('nuvei')) return 'nuvei'
      return ''
    },
    paymentOptionIcon(option) {
      const rail = this.paymentOptionRail(option)
      if (rail === 'paypal') return 'fab fa-paypal'
      if (rail === 'bank_transfer') return 'fas fa-university'
      if (rail === 'stripe' || rail === 'rebill' || rail === 'pagoplux') return 'fas fa-credit-card'
      return (option && option.icon) || 'fas fa-wallet'
    },
    paymentOptionTotal() {
      return this.cartCustomerTotal.toFixed(2)
    },
    officialTicket(listing) {
      return listing && listing.official_ticket ? listing.official_ticket : null
    },
    ticketMaxQuantity(ticket, fallback = 1) {
      return Math.max(1, Number((ticket && (ticket.max_quantity || ticket.maxQuantity)) || fallback || 1))
    },
    localityKey(listing) {
      const officialTicket = this.officialTicket(listing)
      if (officialTicket && officialTicket.id) return `official:${officialTicket.id}`
      const parts = [listing.zone_label, listing.section_label, listing.row_label].filter(Boolean)
      if (parts.length) return `location:${parts.join('|').toLowerCase()}`
      return `listing:${listing.id}`
    },
    localityName(listing) {
      const officialTicket = this.officialTicket(listing)
      if (officialTicket && officialTicket.name) return this.humanizeCompactName(officialTicket.name)
      return this.humanizeCompactName(listing.section_label || listing.zone_label || listing.title || 'Localidad por confirmar')
    },
    humanizeCompactName(value) {
      return String(value || '').replace(/([a-záéíóúñ])([A-ZÁÉÍÓÚÑ])/g, '$1 $2').trim()
    },
    localityCopy(listing) {
      const officialTicket = this.officialTicket(listing)
      if (officialTicket && officialTicket.description) return officialTicket.description
      const label = [listing.zone_label, listing.section_label, listing.row_label].filter(Boolean).join(' · ')
      return label || 'Entradas verificadas por FOMO según disponibilidad de sellers.'
    },
    localityCardCopy(group) {
      if (!group) return 'Elige una localidad para ver entradas protegidas de otros asistentes.'
      const available = Number(group.availableQuantity || 0)
      const max = this.ticketCountLabel(group.maxQuantity || 1)
      if (available > 0) {
        return `Entradas de asistentes para esta localidad. Puedes buscar hasta ${max}; hoy hay ${this.ticketCountLabel(available)} publicada${available === 1 ? '' : 's'}.`
      }
      return `Busca hasta ${max}. Si no hay vendedores activos, FOMO te avisará antes de reservar.`
    },
    localityCardFooter(group) {
      const kind = group && group.isNumbered ? 'Entrada numerada' : 'Entrada general'
      const selected = this.groupQuantity(group)
      if (selected > 0) return `${kind} · objetivo ${this.ticketCountLabel(selected)}`
      return `${kind} · hasta ${this.ticketCountLabel((group && group.maxQuantity) || 1)}`
    },
    listingIsNumbered(listing) {
      return Boolean(
        (Array.isArray(listing.seat_labels_preview) && listing.seat_labels_preview.length)
        || listing.seat_start
        || listing.seat_end
        || listing.row_label
      )
    },
    groupQuantity(group) {
      return this.selectedLocalityKey === group.key ? Number(this.requestedQuantity || 0) : 0
    },
    localityTotalLabel(group) {
      return this.money((Number.parseFloat(group.minPrice || 0) || 0) * this.groupQuantity(group), group.currency || 'USD')
    },
    incrementLocality(group) {
      if (!group) return
      if (this.selectedLocalityKey !== group.key) {
        this.selectedLocalityKey = group.key
        this.requestedQuantity = 1
      } else {
        this.setRequestedQuantity(this.requestedQuantity + 1)
      }
      this.selectedQuantities = {}
      this.splitAccepted = false
    },
    decrementLocality(group) {
      if (!group || this.selectedLocalityKey !== group.key) return
      const nextQuantity = Math.max(0, Number(this.requestedQuantity || 0) - 1)
      if (nextQuantity === 0) {
        this.selectedLocalityKey = ''
        this.requestedQuantity = 1
        this.seatsTogetherOnly = true
        this.selectedQuantities = {}
        this.splitAccepted = false
        return
      }
      this.setRequestedQuantity(nextQuantity)
    },
    selectLocality(group) {
      if (!group) return
      this.selectedLocalityKey = group.key
      this.setRequestedQuantity(Math.min(Math.max(1, this.requestedQuantity), group.maxQuantity || 1))
      this.selectedQuantities = {}
      this.splitAccepted = false
    },
    setRequestedQuantity(value) {
      const maxQuantity = this.selectedLocality ? this.selectedLocality.maxQuantity : 10
      const nextQuantity = Math.max(1, Math.min(Number.parseInt(value, 10) || 1, Math.max(1, maxQuantity || 1)))
      this.requestedQuantity = nextQuantity
      this.selectedQuantities = {}
      this.splitAccepted = false
    },
    scrollActiveStepIntoView() {
      const element = this.$el && this.$el.querySelector ? this.$el.querySelector('.swap-checkout-step') : this.$el
      if (element && element.scrollIntoView) element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    searchMatchingListings() {
      if (!this.selectedLocality) return
      this.setRequestedQuantity(this.requestedQuantity)
      this.selectedQuantities = {}
      this.splitAccepted = false
      this.buyerStage = 'results'
      this.$nextTick(() => this.scrollActiveStepIntoView())
    },
    backToLocalities() {
      this.buyerStage = 'localities'
      this.selectedQuantities = {}
      this.splitAccepted = false
    },
    resultSelectedQuantity(listing) {
      return Number(this.selectedQuantities[listing.id] || 0)
    },
    resultMaxSelectableQuantity(listing) {
      const requested = Number(this.requestedQuantity || 0)
      const current = this.resultSelectedQuantity(listing)
      const selectedElsewhere = Math.max(0, this.selectedCount - current)
      const available = Number(listing.available_units || 0)
      return Math.max(0, Math.min(available, requested - selectedElsewhere))
    },
    canIncrementResultListing(listing) {
      return this.resultSelectedQuantity(listing) < this.resultMaxSelectableQuantity(listing)
    },
    incrementResultListing(listing) {
      this.setQuantity(listing, this.resultSelectedQuantity(listing) + 1)
    },
    decrementResultListing(listing) {
      this.setQuantity(listing, this.resultSelectedQuantity(listing) - 1)
    },
    resultAvailabilityLabel(listing) {
      return this.availabilityLabel(listing.available_units || 0)
    },
    resultPriceLabel(listing) {
      const price = Number.parseFloat(listing.min_total || listing.unit_price || 0) || 0
      return this.money(price, listing.currency || 'USD')
    },
    listingCardCopy(listing) {
      const location = this.locationLabel(listing)
      const delivery = this.deliveryLabel(listing && listing.delivery_method)
      const seats = this.seatLabel(listing)
      return `${location}. ${seats}. Entrega: ${delivery}.`
    },
    selectResultListing(listing) {
      const current = this.resultSelectedQuantity(listing)
      if (current > 0) {
        this.setQuantity(listing, 0)
        return
      }
      this.setQuantity(listing, Math.min(1, this.resultMaxSelectableQuantity(listing)))
    },
    continueBuyerFlow() {
      if (this.buyerStage === 'localities') {
        this.searchMatchingListings()
        return
      }
      this.createQuote()
    },
    lookupCheckoutCustomer() {
      const email = String(this.checkoutForm.customer_email || '').trim()
      this.checkoutError = ''
      this.termsError = false
      this.checkoutCustomer = null
      this.userSearched = false
      this.requireAccount = false
      if (this.checkoutLookupTimer) clearTimeout(this.checkoutLookupTimer)
      if (!/\S+@\S+\.\S+/.test(email)) {
        this.customerLookupLoading = false
        return
      }
      this.customerLookupLoading = true
      this.checkoutLookupTimer = setTimeout(async () => {
        const data = await apiService(`/api/contacts/?email=${encodeURIComponent(email)}`)
        this.customerLookupLoading = false
        if (Array.isArray(data) && data.length) {
          this.applyCheckoutCustomer(data[0])
          return
        }
        this.checkoutCustomer = null
        this.requireAccount = true
        this.userSearched = true
      }, 1250)
    },
    applyCheckoutCustomer(customer) {
      const profile = customer && customer.contact_profile && typeof customer.contact_profile === 'object' ? customer.contact_profile : {}
      this.checkoutCustomer = customer || null
      this.requireAccount = false
      this.userSearched = true
      this.checkoutForm = {
        ...this.checkoutForm,
        customer_first_name: this.checkoutForm.customer_first_name || (customer && customer.first_name) || '',
        customer_last_name: this.checkoutForm.customer_last_name || (customer && customer.last_name) || '',
        customer_phone: this.checkoutForm.customer_phone || profile.phone || '',
        customer_address: this.checkoutForm.customer_address || profile.address || '',
      }
    },
    toggleAcceptedTerms() {
      this.acceptedTerms = !this.acceptedTerms
      if (this.acceptedTerms) this.termsError = false
    },
    openLegalModal(view = 'terms') {
      this.legalModalView = view
      this.showLegalModal = true
    },
    async createCheckoutCustomer() {
      if (this.creatingCustomer) return false
      this.creatingCustomer = true
      this.checkoutError = ''
      const data = await apiService('/api/user/create', 'POST', {
        password: this.checkoutForm.customer_password,
        contact_profile: {
          phone: this.checkoutForm.customer_phone,
          address: this.checkoutForm.customer_address,
          city: '',
        },
        first_name: this.checkoutForm.customer_first_name,
        last_name: this.checkoutForm.customer_last_name,
        email: this.checkoutForm.customer_email,
        username: this.checkoutForm.customer_email,
      })
      this.creatingCustomer = false
      if (data && data.id && !data.err) {
        this.applyCheckoutCustomer(data)
        return true
      }
      this.checkoutError = this.extractError(data, 'Error al crear la cuenta. Revisa los datos e inténtalo nuevamente.')
      return false
    },
    async continueToPayment() {
      if (!this.canContinueToPayment) {
        this.checkoutError = this.userSearched ? 'Completa los datos requeridos para continuar.' : 'Ingresa el correo de compra para buscar o crear la cuenta.'
        return
      }
      if (this.requireAccount) {
        if (!this.acceptedTerms) {
          this.termsError = true
          return
        }
        const created = await this.createCheckoutCustomer()
        if (!created) return
      }
      this.checkoutForm.customer_name = this.checkoutFullName
      this.checkoutError = ''
      this.feedback = ''
      this.activePaymentOptionId = null
      this.pendingPaymentOrder = null
      this.stripeIntent = this.defaultStripeIntent()
      this.cartStage = 'payment'
      this.$nextTick(() => this.scrollActiveStepIntoView())
    },
    selectPaymentOption(option) {
      if (!option || this.checkingOut || this.preparingPayment) return
      const rail = this.paymentOptionRail(option)
      this.activePaymentOptionId = option.id
      this.checkoutForm = { ...this.checkoutForm, payment_option: option.id, payment_rail: rail, provider_reference: '' }
      this.checkoutError = ''
      this.feedback = ''
      this.stripeIntent = this.defaultStripeIntent()
      this.pendingPaymentOrder = null
      if (rail === 'stripe') this.beginStripePayment(option)
    },
    async beginStripePayment(option) {
      if (!option || this.preparingPayment) return
      this.preparingPayment = true
      await this.checkoutCart('', { paymentOption: option, paymentRail: 'stripe', revealOrder: false })
      this.preparingPayment = false
    },
    handlePaypalMessage(tranId) {
      this.checkoutWithProviderReference(this.activePaymentOption, tranId)
    },
    handlePaypalMXNMessage(tranId) {
      this.checkoutWithProviderReference(this.activePaymentOption, tranId)
    },
    handlePaypalMessageForOption(option, tranId) {
      this.checkoutWithProviderReference(option, tranId)
    },
    handlePaypalMXNMessageForOption(option, tranId) {
      this.checkoutWithProviderReference(option, tranId)
    },
    handlePagoPluxMessage(_validator, id, authorizationCode) {
      this.checkoutWithProviderReference(this.activePaymentOption, authorizationCode || id)
    },
    handlePagoPluxArMessage(_validator, id, authorizationCode) {
      this.checkoutWithProviderReference(this.activePaymentOption, authorizationCode || id)
    },
    checkoutWithProviderReference(option, providerReference) {
      const reference = String(providerReference || '').trim()
      if (!option || !reference) {
        this.feedback = 'Completa el pago para crear la orden.'
        this.feedbackType = 'error'
        return
      }
      this.checkoutCart(reference, { paymentOption: option, paymentRail: this.paymentOptionRail(option) })
    },
    handleBankProof(payload) {
      const file = (payload && payload.files && payload.files[0]) || (payload && payload.file) || payload
      if (!file) {
        this.feedback = 'Adjunta un comprobante valido antes de crear la orden.'
        this.feedbackType = 'error'
        return
      }
      this.checkoutCart('', { paymentOption: this.activePaymentOption, paymentRail: 'bank_transfer', proofFile: file })
    },
    returnFromBankPayment() {
      this.activePaymentOptionId = null
      this.checkoutForm = { ...this.checkoutForm, payment_option: null, payment_rail: '', provider_reference: '' }
    },
    quantityOptions(listing) {
      if (this.listingIsPackage(listing)) return Number(listing.available_units || 0) > 0 ? [Number(listing.available_units || 0)] : []
      return Array.from({ length: Math.min(Number(listing.available_units || 0), 10) }, (_item, index) => index + 1)
    },
    ticketCountLabel(count) {
      const value = Number(count || 0)
      return `${value} ${value === 1 ? 'entrada' : 'entradas'}`
    },
    reservedTicketCopy(count) {
      const value = Number(count || 0)
      return `${this.ticketCountLabel(value)} ${value === 1 ? 'apartada' : 'apartadas'} temporalmente`
    },
    availabilityLabel(count) {
      const value = Number(count || 0)
      return value === 1 ? '1 disponible' : `${value} disponibles`
    },
    listingIsPackage(listing) {
      return String((listing && listing.fulfillment_mode) || '') === 'package'
    },
    locationLabel(listing) {
      return [listing.zone_label, listing.section_label, listing.row_label].filter(Boolean).join(' · ') || 'Zona por confirmar'
    },
    seatLabel(listing) {
      if (Array.isArray(listing.seat_labels_preview) && listing.seat_labels_preview.length) return listing.seat_labels_preview.join(', ')
      return [listing.seat_start, listing.seat_end].filter(Boolean).join(' - ') || 'Asiento coordinado'
    },
    deliveryLabel(method) {
      const labels = {
        physical_handoff: 'Entrega física',
        physical_shipping: 'Envío físico',
        app_transfer: 'Transferido por app',
        qr_final_ticket: 'QR entrada final',
        qr_redeem_physical: 'QR canjeable',
        coordinated: 'Entrega física',
        upload: 'QR entrada final',
        external_transfer: 'Transferido por app',
      }
      return labels[method] || 'Entrega protegida'
    },
    paymentStatusLabel(status) {
      const labels = {
        pending: 'pendiente',
        unverified: 'por verificar',
        verified: 'confirmado',
        failed: 'fallido',
        refunded: 'reembolsado',
        chargeback: 'con reclamo',
      }
      return labels[status] || this.humanizeStatus(status).toLowerCase()
    },
    orderStatusLabel(status) {
      const labels = {
        created: 'recibida',
        confirmed: 'confirmada',
        in_delivery: 'en entrega',
        delivered: 'entregada',
        claim_window: 'en seguridad',
        completed: 'completada',
        cancelled: 'cancelada',
        partially_refunded: 'con reembolso parcial',
      }
      return labels[status] || this.humanizeStatus(status).toLowerCase()
    },
    reviewStatusLabel(status) {
      const labels = {
        clear: 'sin novedades',
        watch: 'en observación',
        needs_review: 'en revisión',
        assigned: 'en revisión',
        resolved: 'resuelta',
        blocked: 'pausada',
      }
      return labels[status] || this.humanizeStatus(status).toLowerCase()
    },
    deliveryStatusLabel(status) {
      const labels = {
        pending: 'Pendiente',
        pending_seller_action: 'Por entregar',
        seller_confirmed: 'Entrega registrada',
        delivery_in_progress: 'En proceso',
        delivered: 'Entregada',
        buyer_accessible: 'Lista para ver',
        claim_window_open: 'En seguridad',
        released: 'Completada',
        cancelled: 'Cancelada',
        disputed: 'Con reclamo',
        replaced: 'Reemplazada',
      }
      return labels[status] || this.humanizeStatus(status)
    },
    timelineStatusLabel(status) {
      const labels = {
        complete: 'listo',
        current: 'actual',
        pending: 'pendiente',
        locked: 'bloqueado',
      }
      return labels[status] || this.humanizeStatus(status).toLowerCase()
    },
    deliveryProgressLabel(delivery) {
      return delivery && delivery.buyer_accessible_at ? 'lista para comprador' : 'pendiente FOMO'
    },
    protectedFileCountLabel(count) {
      const value = Number(count || 0)
      return `${value} ${value === 1 ? 'archivo protegido' : 'archivos protegidos'}`
    },
    humanizeStatus(value) {
      if (!value) return 'Pendiente'
      return String(value).replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
    },
    orderDeliverySummary(order) {
      if (!order || !Array.isArray(order.deliveries) || !order.deliveries.length) return 'pendiente'
      const counts = order.deliveries.reduce((acc, delivery) => {
        const status = delivery.status || 'unknown'
        acc[status] = (acc[status] || 0) + 1
        return acc
      }, {})
      return Object.keys(counts).map((status) => `${this.deliveryStatusLabel(status)} ${counts[status]}`).join(' · ')
    },
    visibleArtifactCount(order) {
      if (!order || !Array.isArray(order.deliveries)) return 0
      return order.deliveries.reduce((sum, delivery) => sum + Number((delivery.artifact_summary && delivery.artifact_summary.visible_count) || 0), 0)
    },
    orderCustomer(order) {
      return (order && order.metadata && order.metadata.customer) || {}
    },
    orderCustomerName(order) {
      return this.orderCustomer(order).name || this.checkoutForm.customer_name || ''
    },
    orderCustomerEmail(order) {
      return this.orderCustomer(order).email || this.checkoutForm.customer_email || ''
    },
    orderCustomerPhone(order) {
      return this.orderCustomer(order).phone || this.checkoutForm.customer_phone || ''
    },
    orderPaymentRail(order) {
      const verification = order && order.payment_verification ? order.payment_verification : {}
      const metadata = order && order.metadata ? order.metadata : {}
      return verification.rail || metadata.payment_rail || ''
    },
    canPayStripeOrder(order) {
      return Boolean(order && order.order_number && order.payment_status !== 'verified' && this.orderPaymentRail(order) === 'stripe')
    },
    allocationForItem(order, item) {
      const allocations = Array.isArray(order && order.finance_allocations) ? order.finance_allocations : []
      return allocations.find((allocation) => Number(allocation.order_item) === Number(item && item.id)) || null
    },
    claimableOrderItems(order) {
      if (!order || order.payment_status !== 'verified' || !Array.isArray(order.items)) return []
      const blockedStatuses = ['releasable', 'reserved_for_payout', 'paid', 'refunded', 'cancelled']
      return order.items.filter((item) => {
        if (item.delivery_status === 'refunded') return false
        const allocation = this.allocationForItem(order, item)
        return !allocation || !blockedStatuses.includes(allocation.status)
      })
    },
    canOpenDispute(order) {
      if (!order || ['releasable', 'reserved', 'paid'].includes(order.payout_status)) return false
      return this.claimableOrderItems(order).length > 0
    },
    orderItemLabel(item) {
      const snapshot = (item && item.snapshot) || {}
      const seat = [snapshot.section_label, snapshot.row_label, snapshot.seat_label].filter(Boolean).join(' · ')
      return seat || `Ticket #${item && item.id}`
    },
    syncDisputeForm(order) {
      const items = this.claimableOrderItems(order)
      const existingItem = items.find((item) => Number(item.id) === Number(this.disputeForm.order_item))
      this.disputeForm = {
        ...this.disputeForm,
        customer_email: this.disputeForm.customer_email || this.orderCustomerEmail(order),
        order_item: existingItem ? existingItem.id : (items[0] && items[0].id) || '',
      }
    },
    syncArtifactAccessEmail(order) {
      if (!this.artifactAccessEmail) this.artifactAccessEmail = this.orderCustomerEmail(order)
    },
    setQuantity(listing, value) {
      const requested = Number.parseInt(value, 10) || 0
      const max = this.resultMaxSelectableQuantity(listing)
      const quantity = Math.max(0, Math.min(requested, max))
      const nextQuantities = { ...this.selectedQuantities }
      if (quantity > 0) {
        nextQuantities[listing.id] = quantity
      } else {
        delete nextQuantities[listing.id]
      }
      this.selectedQuantities = nextQuantities
      this.splitAccepted = false
    },
    async loadListings() {
      if (!this.eventSlug) return
      this.loading = true
      this.feedback = ''
      const data = await apiService(`/api/swap/events/${encodeURIComponent(this.eventSlug)}/listings/`)
      this.loading = false
      if (data && Array.isArray(data.listings)) {
        this.listings = data.listings
        this.officialTickets = Array.isArray(data.official_tickets) ? data.official_tickets : []
        return
      }
      this.listings = []
      this.officialTickets = []
      this.feedback = this.extractError(data, 'No pudimos cargar entradas disponibles.')
      this.feedbackType = 'error'
    },
    async restoreLastOrder() {
      if (typeof window === 'undefined' || !window.localStorage) return
      const orderNumber = window.localStorage.getItem(this.orderStorageKey()) || ''
      if (orderNumber) await this.loadOrder(orderNumber, { quiet: true })
    },
    async loadOrder(orderNumber, options = {}) {
      const normalized = String(orderNumber || '').trim()
      if (!normalized || this.loadingOrder) return
      this.loadingOrder = true
      if (!options.quiet) this.feedback = ''
      const data = await apiService(`/api/swap/orders/${encodeURIComponent(normalized)}/`)
      this.loadingOrder = false
      if (data && data.order_number && !data.err) {
        this.order = data
        this.cart = null
        this.orderLookupNumber = data.order_number
        this.stripeIntent = this.defaultStripeIntent()
        this.syncDisputeForm(data)
        this.syncArtifactAccessEmail(data)
        this.deliveryArtifacts = []
        this.artifactAccessError = ''
        if (data.payment_status === 'verified') await this.loadBuyerMessages()
        if (!options.quiet) {
          this.feedback = 'Orden cargada.'
          this.feedbackType = 'success'
        }
        return
      }
      if (!options.quiet) {
        this.feedback = this.extractError(data, 'No pudimos encontrar esa orden.')
        this.feedbackType = 'error'
      }
    },
    lookupOrder() {
      this.loadOrder(this.orderLookupNumber)
    },
    refreshOrder() {
      if (!this.order || !this.order.order_number) return
      this.loadOrder(this.order.order_number)
    },
    async createQuote() {
      if (!this.selectedItems.length || this.quoting) return
      this.quoting = true
      this.feedback = ''
      const payload = {
        items: this.selectedItems.map((item) => ({ listing: item.listing.id, quantity: item.quantity })),
        split_seating_accepted: this.splitAccepted,
        customer_session: window.localStorage.getItem('fomo_landing_visitor_token') || '',
      }
      const data = await apiService(`/api/swap/events/${encodeURIComponent(this.eventSlug)}/quote/`, 'POST', payload)
      this.quoting = false
      if (data && data.token && !data.err) {
        this.cart = data
        this.cartStage = 'details'
        this.order = null
        this.checkoutForm = this.defaultCheckoutForm()
        this.checkoutCustomer = null
        this.userSearched = false
        this.requireAccount = false
        this.acceptedTerms = false
        this.termsError = false
        this.checkoutError = ''
        this.feedback = 'Reserva creada. Tus entradas quedan apartadas temporalmente.'
        this.feedbackType = 'success'
        this.selectedQuantities = {}
        this.splitAccepted = false
        await this.loadListings()
        this.$emit('quote-held', data)
        this.$nextTick(() => this.scrollActiveStepIntoView())
        return
      }
      this.feedback = this.extractError(data, 'No pudimos crear la reserva.')
      this.feedbackType = 'error'
    },
    async checkoutCart(providerReference = '', options = {}) {
      if (!this.cart || !this.cart.token || this.checkingOut) return
      const paypalReference = ['string', 'number'].includes(typeof providerReference) ? String(providerReference).trim() : ''
      const paymentOption = options.paymentOption || this.activePaymentOption
      const paymentRail = options.paymentRail || this.paymentOptionRail(paymentOption)
      const proofFile = options.proofFile || null
      const revealOrder = options.revealOrder !== false
      if (!paymentOption || !paymentRail) {
        this.feedback = 'Selecciona un metodo de pago habilitado para este evento.'
        this.feedbackType = 'error'
        return
      }
      if (paymentRail !== 'stripe' && !paypalReference && !proofFile) {
        this.feedback = 'Completa el pago o sube el comprobante antes de crear la orden.'
        this.feedbackType = 'error'
        return
      }
      this.checkingOut = true
      this.feedback = ''
      this.checkoutForm.customer_name = this.checkoutFullName || this.checkoutForm.customer_name
      if (paypalReference) this.checkoutForm.provider_reference = paypalReference
      const payload = {
        customer: {
          id: this.checkoutCustomer && this.checkoutCustomer.id ? this.checkoutCustomer.id : undefined,
          name: this.checkoutForm.customer_name,
          email: this.checkoutForm.customer_email,
          phone: this.checkoutForm.customer_phone,
          address: this.checkoutForm.customer_address,
        },
        customer_name: this.checkoutForm.customer_name,
        customer_email: this.checkoutForm.customer_email,
        customer_phone: this.checkoutForm.customer_phone,
        payment_option: paymentOption.id,
        payment_rail: paymentRail,
        provider_reference: paypalReference || this.checkoutForm.provider_reference,
      }
      let requestPayload = payload
      if (proofFile) {
        requestPayload = new FormData()
        requestPayload.append('customer_name', payload.customer_name || '')
        requestPayload.append('customer_email', payload.customer_email || '')
        requestPayload.append('customer_phone', payload.customer_phone || '')
        requestPayload.append('payment_option', String(paymentOption.id))
        requestPayload.append('payment_rail', paymentRail)
        requestPayload.append('provider_reference', payload.provider_reference || '')
        requestPayload.append('proof_file', proofFile)
      }
      const endpoint = `/api/swap/carts/${encodeURIComponent(this.cart.token)}/checkout/`
      const data = proofFile ? await apiServiceForm(endpoint, 'POST', requestPayload) : await apiService(endpoint, 'POST', requestPayload)
      this.checkingOut = false
      if (data && data.order_number && !data.err) {
        if (revealOrder) {
          this.order = data
          this.orderLookupNumber = data.order_number
        } else {
          this.pendingPaymentOrder = data
        }
        this.stripeIntent = this.defaultStripeIntent()
        this.syncDisputeForm(data)
        this.syncArtifactAccessEmail(data)
        this.deliveryArtifacts = []
        this.artifactAccessError = ''
        this.feedback = revealOrder ? 'Orden creada. FOMO verificará el pago antes de habilitar la entrega.' : 'Formulario de pago listo.'
        this.feedbackType = 'success'
        if (revealOrder) await this.loadListings()
        if (paymentRail === 'stripe') await this.prepareStripePayment(data)
        return
      }
      this.feedback = this.extractError(data, 'No pudimos crear la orden.')
      this.feedbackType = 'error'
    },
    async releaseCart() {
      if (!this.cart || !this.cart.token || this.releasing) return
      this.releasing = true
      const data = await apiService(`/api/swap/carts/${encodeURIComponent(this.cart.token)}/release/`, 'POST', {})
      this.releasing = false
      if (data && data.status === 'cancelled') {
        this.cart = null
        this.cartStage = 'details'
        this.order = null
        this.pendingPaymentOrder = null
        this.activePaymentOptionId = null
        this.preparingPayment = false
        this.buyerStage = 'localities'
        this.selectedLocalityKey = ''
        this.requestedQuantity = 1
        this.seatsTogetherOnly = true
        this.selectedQuantities = {}
        this.splitAccepted = false
        this.checkoutForm = this.defaultCheckoutForm()
        this.checkoutCustomer = null
        this.userSearched = false
        this.requireAccount = false
        this.acceptedTerms = false
        this.termsError = false
        this.checkoutError = ''
        this.feedback = 'Reserva liberada.'
        this.feedbackType = 'success'
        await this.loadListings()
        return
      }
      this.feedback = this.extractError(data, 'No pudimos liberar la reserva.')
      this.feedbackType = 'error'
    },
    async startAnotherPurchase() {
      this.cart = null
      this.cartStage = 'details'
      this.order = null
      this.pendingPaymentOrder = null
      this.activePaymentOptionId = null
      this.preparingPayment = false
      this.buyerStage = 'localities'
      this.selectedLocalityKey = ''
      this.requestedQuantity = 1
      this.seatsTogetherOnly = true
      this.selectedQuantities = {}
      this.splitAccepted = false
      this.checkoutForm = this.defaultCheckoutForm()
      this.checkoutCustomer = null
      this.userSearched = false
      this.requireAccount = false
      this.acceptedTerms = false
      this.termsError = false
      this.checkoutError = ''
      this.feedback = ''
      this.stripeIntent = this.defaultStripeIntent()
      await this.loadListings()
      this.$nextTick(() => this.scrollActiveStepIntoView())
    },
    async prepareStripePayment(orderOverride = null) {
      const targetOrder = orderOverride || this.pendingPaymentOrder || this.order
      if (!this.canPayStripeOrder(targetOrder) || this.loadingStripeIntent) return
      this.loadingStripeIntent = true
      this.feedback = ''
      const data = await apiService(`/api/swap/orders/${encodeURIComponent(targetOrder.order_number)}/stripe-intent/`, 'POST', {})
      this.loadingStripeIntent = false
      if (data && data.client_secret && data.publishable_key && !data.err) {
        this.stripeIntent = {
          paymentIntentId: data.payment_intent_id || '',
          clientSecret: data.client_secret,
          publishableKey: data.publishable_key,
          amount: data.amount || targetOrder.totals.customer_total || 0,
          currency: data.currency || targetOrder.totals.currency || 'USD',
        }
        this.feedback = 'Formulario de tarjeta listo.'
        this.feedbackType = 'success'
        return
      }
      this.feedback = this.extractError(data, 'No pudimos preparar el pago con tarjeta.')
      this.feedbackType = 'error'
    },
    async handleStripeCompleted() {
      const targetOrder = this.pendingPaymentOrder || this.order
      if (!targetOrder || !targetOrder.order_number) return
      this.loadingOrder = true
      const data = await apiService(`/api/swap/orders/${encodeURIComponent(targetOrder.order_number)}/stripe-status/`, 'POST', {})
      this.loadingOrder = false
      if (data && data.order && !data.err) {
        this.order = data.order
        this.orderLookupNumber = data.order.order_number
        this.pendingPaymentOrder = null
        this.stripeIntent = this.defaultStripeIntent()
        this.syncDisputeForm(data.order)
        this.syncArtifactAccessEmail(data.order)
        this.deliveryArtifacts = []
        this.artifactAccessError = ''
        if (data.order.payment_status === 'verified') await this.loadBuyerMessages()
        this.feedback = data.order.payment_status === 'verified' ? 'Pago confirmado. FOMO habilitará la entrega.' : `El pago con tarjeta sigue ${data.stripe_status || 'pendiente'}.`
        this.feedbackType = 'success'
        return
      }
      this.feedback = this.extractError(data, 'El pago se confirmó, pero no pudimos sincronizar la orden. Usa Actualizar estado.')
      this.feedbackType = 'error'
    },
    async submitDispute() {
      if (!this.order || !this.order.order_number || this.submittingDispute) return
      this.submittingDispute = true
      this.feedback = ''
      const payload = {
        customer_email: this.disputeForm.customer_email,
        order_item: Number(this.disputeForm.order_item),
        reason: this.disputeForm.reason,
        description: this.disputeForm.description,
      }
      const data = await apiService(`/api/swap/orders/${encodeURIComponent(this.order.order_number)}/disputes/`, 'POST', payload)
      this.submittingDispute = false
      if (data && data.id && !data.err) {
        this.feedback = 'Reclamo abierto. FOMO revisará el caso y pausará el pago mientras se resuelve.'
        this.feedbackType = 'success'
        this.disputeForm = { ...this.defaultDisputeForm(), customer_email: this.orderCustomerEmail(this.order) }
        await this.loadOrder(this.order.order_number, { quiet: true })
        return
      }
      this.feedback = this.extractError(data, 'No pudimos abrir el reclamo.')
      this.feedbackType = 'error'
    },
    async loadDeliveryArtifacts() {
      if (!this.order || !this.order.order_number || this.loadingArtifacts) return
      this.loadingArtifacts = true
      this.artifactAccessError = ''
      const data = await apiService(`/api/swap/orders/${encodeURIComponent(this.order.order_number)}/delivery-artifacts/`, 'POST', {
        customer_email: this.artifactAccessEmail,
      })
      this.loadingArtifacts = false
      if (data && Array.isArray(data.artifacts) && !data.err) {
        this.deliveryArtifacts = data.artifacts
        this.feedback = data.artifacts.length ? 'Entrega protegida cargada.' : 'Aún no hay archivos liberados por FOMO.'
        this.feedbackType = 'success'
        return
      }
      this.deliveryArtifacts = []
      this.artifactAccessError = this.extractError(data, 'No pudimos validar la entrega.')
    },
    copyOrderNumber() {
      if (!this.order || !this.order.order_number || !navigator.clipboard) return
      navigator.clipboard.writeText(this.order.order_number)
      this.feedback = 'Número de orden copiado.'
      this.feedbackType = 'success'
    },
    setBuyerMessageDraft(conversationId, value) {
      this.buyerMessageDrafts = { ...this.buyerMessageDrafts, [conversationId]: value }
    },
    async loadBuyerMessages() {
      if (!this.order || !this.order.order_number || this.loadingMessages) return
      const email = this.orderCustomerEmail(this.order)
      if (!email) {
        this.buyerMessageError = 'Necesitamos el email de compra para cargar mensajes.'
        return
      }
      this.loadingMessages = true
      this.buyerMessageError = ''
      const data = await apiService(`/api/swap/orders/${encodeURIComponent(this.order.order_number)}/messages/?customer_email=${encodeURIComponent(email)}`)
      this.loadingMessages = false
      if (data && Array.isArray(data.conversations) && !data.err) {
        this.buyerConversations = data.conversations
        return
      }
      this.buyerConversations = []
      this.buyerMessageError = this.extractError(data, 'No pudimos cargar tus mensajes.')
    },
    async sendBuyerMessage(conversation) {
      if (!this.order || !conversation || !conversation.id) return
      const body = String(this.buyerMessageDrafts[conversation.id] || '').trim()
      const email = this.orderCustomerEmail(this.order)
      if (!body || !email || this.sendingMessageKey) return
      this.sendingMessageKey = `buyer-${conversation.id}`
      this.buyerMessageError = ''
      const data = await apiService(`/api/swap/orders/${encodeURIComponent(this.order.order_number)}/messages/`, 'POST', {
        customer_email: email,
        conversation: conversation.id,
        body,
      })
      this.sendingMessageKey = ''
      if (data && data.id && !data.err) {
        this.setBuyerMessageDraft(conversation.id, '')
        await this.loadBuyerMessages()
        return
      }
      this.buyerMessageError = this.extractError(data, 'No pudimos enviar tu mensaje a FOMO.')
    },
  },
}
</script>

<style scoped>
.swap-marketplace { display: block; margin-top: 0; padding: 0; background: transparent; border: 0; box-shadow: none; color: inherit; }
.swap-checkout-step { width: 100%; scroll-margin-top: 220px; }
.swap-checkout-step--details, .swap-checkout-step--details * { font-family: inherit; }
.swap-details-form { display: grid; gap: 22px; }
.swap-step-intro { text-align: left; gap: 10px; }
.swap-step-intro .checkout-step__hint { max-width: 760px; margin: 0; color: rgba(232, 238, 252, 0.82); font-size: 1rem; line-height: 1.62; }
.swap-details-guard { width: min(860px, 100%); margin: 0 auto; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
.swap-details-guard article { display: grid; gap: 6px; padding: 16px; border-radius: 22px; background: linear-gradient(135deg, rgba(120, 240, 202, 0.1), rgba(109, 168, 255, 0.07)); border: 1px solid rgba(196, 220, 255, 0.14); text-align: left; box-shadow: inset 0 1px 0 rgba(255,255,255,0.06); }
.swap-details-guard span { display: inline-flex; align-items: center; gap: 8px; color: rgba(167, 243, 208, 0.9); font-size: 0.68rem; font-weight: 900; letter-spacing: 0.13em; text-transform: uppercase; }
.swap-details-guard strong { color: #f8fbff; font-size: 0.92rem; line-height: 1.4; }
.swap-checkout-step--details .checkout-form-grid, .swap-checkout-step--details .checkout-step__section, .swap-checkout-step--details .checkout-check { width: min(860px, 100%); margin-left: auto; margin-right: auto; }
.swap-checkout-step--details .checkout-step__section { display: grid; gap: 16px; padding-top: 6px; }
.swap-checkout-step--details .checkout-step__section-title { margin: 0; color: #f8fbff; letter-spacing: -0.03em; }
.swap-checkout-step--details .checkout-step__section-text { margin: 0; color: rgba(226, 238, 250, 0.76); font-size: 0.96rem; line-height: 1.58; }
.swap-checkout-step--details .checkout-guest-card { padding: 20px; }
.swap-checkout-step--details .checkout-guest-card__header { gap: 8px; }
.swap-checkout-step--details .checkout-step__actions--dock { margin-top: 4px; }
.swap-marketplace__header { display: flex; justify-content: space-between; gap: 14px; align-items: flex-start; }
.swap-marketplace__header span, .swap-marketplace__price-row small, .swap-marketplace__cart span { color: rgba(167, 243, 208, 0.86); font-size: 0.7rem; font-weight: 900; letter-spacing: 0.14em; text-transform: uppercase; }
.swap-marketplace__header h3, .swap-marketplace__listing h4, .swap-marketplace__cart h4 { margin: 0; color: #fff; }
.swap-marketplace__header p, .swap-marketplace__listing p, .swap-marketplace__price-row span, .swap-marketplace__cart p { margin: 0; color: rgba(226, 238, 250, 0.74); }
.swap-marketplace__refresh, .swap-marketplace__hold-button, .swap-marketplace__cart button { border: 1px solid rgba(196, 220, 255, 0.18); border-radius: 999px; padding: 10px 14px; background: rgba(255,255,255,0.07); color: #eaf2ff; font-weight: 900; }
.swap-marketplace__hold-button { background: linear-gradient(135deg, #78f0ca, #6da8ff); border: none; color: #06101a; }
.swap-marketplace__hold-button:disabled, .swap-marketplace__refresh:disabled, .swap-marketplace__cart button:disabled { opacity: 0.55; cursor: not-allowed; }
.swap-marketplace__feedback, .swap-marketplace__empty { padding: 12px 14px; border-radius: 18px; font-weight: 800; }
.swap-marketplace__feedback.is-success { background: rgba(52, 211, 153, 0.14); color: #b8ffe5; }
.swap-marketplace__feedback.is-error { background: rgba(248, 113, 113, 0.16); color: #ffd4d4; }
.swap-marketplace__empty { background: rgba(255,255,255,0.06); color: rgba(226, 238, 250, 0.76); }
.swap-marketplace__empty--notice { display: grid; gap: 6px; padding: 18px; border: 1px solid rgba(251, 191, 36, 0.2); background: linear-gradient(135deg, rgba(251, 191, 36, 0.1), rgba(109, 168, 255, 0.06)); }
.swap-marketplace__empty--notice p { margin: 0; color: rgba(226, 238, 250, 0.78); line-height: 1.5; font-weight: 700; }
.swap-marketplace__empty-title { color: #fff8d6; font-size: 1rem; }
.swap-marketplace__steps { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
.swap-marketplace__steps .is-step { display: flex; align-items: center; justify-content: center; min-height: 42px; padding: 8px 10px; border-radius: 999px; background: rgba(255,255,255,0.055); color: rgba(226,238,250,0.62); border: 1px solid rgba(196,220,255,0.12); font-size: 0.72rem; font-weight: 900; letter-spacing: 0.1em; text-transform: uppercase; }
.swap-marketplace__steps .is-step.is-active { background: linear-gradient(135deg, rgba(120,240,202,0.2), rgba(109,168,255,0.16)); color: #f8fbff; border-color: rgba(120,240,202,0.34); }
.swap-marketplace__steps .is-step.is-done { color: #b8ffe5; border-color: rgba(120,240,202,0.22); }
.swap-marketplace__locality-step, .swap-marketplace__results-step { display: grid; gap: 14px; }
.swap-marketplace__stage-head, .swap-marketplace__results-head { display: flex; justify-content: space-between; gap: 14px; align-items: flex-start; }
.swap-marketplace__stage-head { display: grid; }
.swap-marketplace__stage-head span, .swap-marketplace__results-head span, .swap-marketplace__search-panel span, .swap-marketplace__quantity-picker span { color: rgba(167,243,208,0.86); font-size: 0.68rem; font-weight: 900; letter-spacing: 0.14em; text-transform: uppercase; }
.swap-marketplace__stage-head h4, .swap-marketplace__results-head h4, .swap-marketplace__search-panel strong { margin: 0; color: #fff; }
.swap-marketplace__stage-head p, .swap-marketplace__results-head p, .swap-marketplace__search-panel small { margin: 0; color: rgba(226,238,250,0.72); }
.swap-marketplace__availability-note { margin: 0; padding: 12px 14px; border-radius: 18px; background: rgba(120, 240, 202, 0.08); border: 1px solid rgba(120, 240, 202, 0.16); color: rgba(226, 238, 250, 0.82); font-weight: 800; }
.swap-marketplace__locality-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.swap-marketplace__locality-card { display: grid; gap: 10px; padding: 15px; border-radius: 22px; background: rgba(255,255,255,0.065); border: 1px solid rgba(196,220,255,0.13); cursor: pointer; transition: transform .18s ease, border-color .18s ease, background .18s ease; }
.swap-marketplace__locality-card:hover, .swap-marketplace__locality-card.is-selected { transform: translateY(-1px); border-color: rgba(120,240,202,0.38); background: linear-gradient(145deg, rgba(120,240,202,0.12), rgba(109,168,255,0.08)); }
.swap-marketplace__locality-top { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.swap-marketplace__locality-top span { color: rgba(167,243,208,0.78); font-size: 0.66rem; font-weight: 900; letter-spacing: 0.13em; text-transform: uppercase; }
.swap-marketplace__locality-top strong { color: #fff; }
.swap-marketplace__locality-card h4 { margin: 0; color: #fff; font-size: 1.08rem; }
.swap-marketplace__locality-card p { margin: 0; color: rgba(226,238,250,0.72); line-height: 1.45; }
.swap-marketplace__locality-meta { display: flex; flex-wrap: wrap; gap: 8px; }
.swap-marketplace__locality-meta span { display: inline-flex; align-items: center; gap: 6px; padding: 6px 9px; border-radius: 999px; background: rgba(255,255,255,0.08); color: rgba(226,238,250,0.78); font-size: 0.76rem; font-weight: 800; }
.swap-marketplace__search-panel { display: grid; grid-template-columns: minmax(0, 1fr) auto auto auto; gap: 12px; align-items: center; padding: 14px; border-radius: 22px; background: rgba(4,11,21,0.72); border: 1px solid rgba(120,240,202,0.2); }
.swap-marketplace__search-panel > div:first-child { display: grid; gap: 4px; }
.swap-marketplace__quantity-picker { display: grid; gap: 6px; min-width: 136px; }
.swap-marketplace__quantity-picker > div { display: inline-grid; grid-template-columns: 38px 48px 38px; align-items: center; border-radius: 999px; background: rgba(255,255,255,0.07); border: 1px solid rgba(196,220,255,0.14); overflow: hidden; }
.swap-marketplace__quantity-picker button { min-height: 38px; border: none; background: transparent; color: #f8fbff; font-size: 1.2rem; font-weight: 900; }
.swap-marketplace__quantity-picker button:disabled { opacity: .35; }
.swap-marketplace__quantity-picker strong { text-align: center; color: #fff; }
.swap-marketplace__together-check { display: inline-flex; align-items: center; gap: 8px; color: rgba(226,238,250,0.86); font-weight: 900; white-space: nowrap; }
.swap-marketplace__listing.is-selected { border-color: rgba(120,240,202,0.42); background: linear-gradient(145deg, rgba(120,240,202,0.12), rgba(109,168,255,0.08)); }
.swap-marketplace__select-result { border: none; border-radius: 999px; padding: 10px 14px; background: rgba(255,255,255,0.1); color: #f8fbff; font-weight: 900; }
.swap-marketplace__listing.is-selected .swap-marketplace__select-result { background: linear-gradient(135deg, #78f0ca, #6da8ff); color: #06101a; }
.swap-marketplace__lookup { display: grid; grid-template-columns: minmax(0, 1fr) minmax(180px, 0.55fr) auto; gap: 10px; align-items: center; padding: 12px; border-radius: 20px; background: rgba(255,255,255,0.055); border: 1px solid rgba(196, 220, 255, 0.13); }
.swap-marketplace__lookup span { color: rgba(167, 243, 208, 0.86); font-size: 0.7rem; font-weight: 900; letter-spacing: 0.14em; text-transform: uppercase; }
.swap-marketplace__lookup p { margin: 0; color: rgba(226, 238, 250, 0.72); }
.swap-marketplace__lookup input { border: 1px solid rgba(196, 220, 255, 0.18); border-radius: 14px; background: rgba(5, 10, 18, 0.74); color: #fff; padding: 10px 12px; }
.swap-marketplace__lookup button { border: none; border-radius: 999px; padding: 10px 14px; background: linear-gradient(135deg, #78f0ca, #6da8ff); color: #06101a; font-weight: 900; }
.swap-marketplace__grid { display: grid; gap: 12px; }
.swap-marketplace__listing { display: grid; gap: 10px; padding: 14px; border-radius: 22px; background: rgba(255,255,255,0.07); border: 1px solid rgba(196, 220, 255, 0.13); }
.swap-marketplace__listing-top, .swap-marketplace__price-row, .swap-marketplace__quote-bar, .swap-marketplace__cart { display: flex; justify-content: space-between; gap: 12px; align-items: center; }
.swap-marketplace__listing-top span { padding: 6px 9px; border-radius: 999px; background: rgba(255,255,255,0.08); color: rgba(226, 238, 250, 0.78); font-size: 0.76rem; font-weight: 800; }
.swap-marketplace__meta { display: flex; flex-wrap: wrap; gap: 8px; color: rgba(226, 238, 250, 0.78); font-size: 0.88rem; }
.swap-marketplace__meta span { display: inline-flex; align-items: center; gap: 7px; }
.swap-marketplace__instruction-card { display: grid; gap: 4px; padding: 11px 12px; border-radius: 16px; background: rgba(120, 240, 202, 0.09); border: 1px solid rgba(120, 240, 202, 0.18); }
.swap-marketplace__instruction-card strong { color: #d8fff2; font-size: 0.88rem; }
.swap-marketplace__instruction-card span { color: rgba(226, 238, 250, 0.72); font-size: 0.84rem; line-height: 1.45; }
.swap-marketplace__price-row label { display: grid; gap: 5px; color: rgba(226, 238, 250, 0.78); font-weight: 800; }
.swap-marketplace__price-row select { border: 1px solid rgba(196, 220, 255, 0.18); border-radius: 14px; background: rgba(5, 10, 18, 0.74); color: #fff; padding: 8px 10px; }
.swap-marketplace__quote-bar { position: sticky; bottom: 12px; z-index: 5; padding: 14px; border-radius: 22px; background: rgba(4, 11, 21, 0.94); border: 1px solid rgba(120, 240, 202, 0.24); box-shadow: 0 18px 55px rgba(2, 8, 23, 0.32); }
.swap-marketplace__quote-bar strong { color: #fff; display: block; }
.swap-marketplace__quote-bar small { color: rgba(226, 238, 250, 0.66); }
.swap-marketplace__split-check { display: inline-flex; gap: 8px; align-items: center; color: rgba(226, 238, 250, 0.82); font-size: 0.84rem; font-weight: 800; }
.swap-marketplace__cart { padding: 14px; border-radius: 22px; background: rgba(120, 240, 202, 0.1); border: 1px solid rgba(120, 240, 202, 0.2); }
.swap-marketplace__cart { align-items: stretch; }
.swap-marketplace__checkout-form { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; min-width: min(520px, 100%); }
.swap-marketplace__checkout-form label { display: grid; gap: 5px; color: rgba(226, 238, 250, 0.78); font-size: 0.78rem; font-weight: 800; }
.swap-marketplace__checkout-form input, .swap-marketplace__checkout-form select { border: 1px solid rgba(196, 220, 255, 0.18); border-radius: 14px; background: rgba(5, 10, 18, 0.74); color: #fff; padding: 9px 10px; }
.swap-marketplace__checkout-form--normal { width: min(760px, 100%); margin: 0 auto 18px; }
.swap-marketplace__checkout-form--reference { margin-top: 4px; }
.swap-checkout-step .checkout-step__trust .checkout-step__trust-badge--swap { color: rgba(245, 238, 255, 0.9); background: radial-gradient(circle at 18% 0%, rgba(191, 146, 255, 0.26), transparent 44%), linear-gradient(135deg, rgba(126, 87, 255, 0.18), rgba(204, 132, 255, 0.1)); border-color: rgba(190, 154, 255, 0.26); box-shadow: 0 14px 34px rgba(93, 55, 190, 0.16), inset 0 1px 0 rgba(255,255,255,0.08); letter-spacing: 0.01em; text-transform: none; }
.swap-checkout-step .checkout-step__trust .checkout-step__trust-badge--swap::before { background: #c8a6ff; box-shadow: 0 0 14px rgba(182, 131, 255, 0.62); }
.swap-marketplace__account-form { width: min(760px, 100%); margin: 0 auto 18px; text-align: left; }
.swap-marketplace__field-hint { display: block; margin-top: 8px; color: rgba(214, 224, 248, 0.78); font-size: 0.82rem; line-height: 1.45; }
.swap-marketplace__field-hint.is-success { color: #a7f3d0; }
.swap-marketplace__field-hint.is-warning { color: #fde68a; }
.swap-marketplace__terms-check, .swap-marketplace__buyer-section { width: min(760px, 100%); margin: 0 auto 18px; text-align: left; }
.checkout-method-card.is-selected { border-color: rgba(120, 240, 202, 0.35); box-shadow: 0 0 0 1px rgba(120, 240, 202, 0.18); }
.swap-marketplace__payment-stack { width: min(760px, 100%); margin: 0 auto; display: grid; gap: 18px; }
.swap-marketplace__method-buttons { display: grid; gap: 10px; padding-bottom: 0 !important; }
.swap-marketplace__payment-copy { margin: -4px 0 14px; color: rgba(226, 238, 250, 0.74); font-size: 0.9rem; line-height: 1.5; }
.swap-marketplace__paypal-card { padding: 18px; text-align: left; }
.swap-marketplace__field-wide { grid-column: 1 / -1; }
.swap-marketplace__cart-actions { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; justify-content: flex-end; }
.swap-marketplace__cart-actions button:not(.checkout-step__button), .swap-marketplace__account-link { border: 1px solid rgba(196, 220, 255, 0.2); border-radius: 999px; padding: 10px 16px; background: rgba(255,255,255,0.07); color: #f8fbff; font-weight: 900; box-shadow: inset 0 0 0 1px rgba(255,255,255,0.04); text-decoration: none; }
.swap-marketplace__cart-actions button:not(.checkout-step__button):hover, .swap-marketplace__account-link:hover { border-color: rgba(120, 240, 202, 0.34); background: rgba(120, 240, 202, 0.1); color: #f8fbff; }
.swap-marketplace__cart-actions button:not(.checkout-step__button):disabled { opacity: .55; cursor: not-allowed; }
.swap-marketplace__cart--order { background: rgba(109, 168, 255, 0.12); border-color: rgba(109, 168, 255, 0.26); }
.swap-marketplace__order-created { width: min(980px, calc(100% - 28px)); margin: 0 auto; display: grid; gap: 16px; }
.swap-marketplace__order-status, .swap-marketplace__delivery-list { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
.swap-marketplace__order-status span, .swap-marketplace__delivery-list article { border: 1px solid rgba(196, 220, 255, 0.14); border-radius: 14px; padding: 8px 10px; background: rgba(255,255,255,0.06); color: rgba(226, 238, 250, 0.78); font-size: 0.82rem; font-weight: 800; }
.swap-marketplace__delivery-list article { display: grid; gap: 2px; }
.swap-marketplace__delivery-list strong { color: #fff; }
.swap-marketplace__delivery-list small { color: rgba(226, 238, 250, 0.68); }
.swap-marketplace__timeline { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 8px; margin-top: 12px; }
.swap-marketplace__timeline-step { display: grid; gap: 5px; padding: 11px; border-radius: 16px; background: rgba(255,255,255,0.055); border: 1px solid rgba(196, 220, 255, 0.12); }
.swap-marketplace__timeline-step small { color: rgba(226, 238, 250, 0.58); font-size: 0.64rem; font-weight: 900; letter-spacing: 0.14em; text-transform: uppercase; }
.swap-marketplace__timeline-step strong { color: #fff; font-size: 0.85rem; }
.swap-marketplace__timeline-step span { color: rgba(226, 238, 250, 0.68); font-size: 0.76rem; line-height: 1.35; }
.swap-marketplace__timeline-step.is-complete { background: rgba(52, 211, 153, 0.11); border-color: rgba(52, 211, 153, 0.22); }
.swap-marketplace__timeline-step.is-current { background: rgba(109, 168, 255, 0.12); border-color: rgba(109, 168, 255, 0.24); }
.swap-marketplace__timeline-step.is-locked { opacity: 0.62; }
.swap-marketplace__artifact-panel { display: grid; gap: 12px; margin-top: 14px; padding: 14px; border-radius: 20px; border: 1px solid rgba(120, 240, 202, 0.24); background: linear-gradient(135deg, rgba(120, 240, 202, 0.12), rgba(109, 168, 255, 0.08)); }
.swap-marketplace__artifact-email { display: grid; gap: 6px; color: rgba(226, 238, 250, 0.78); font-size: 0.78rem; font-weight: 800; }
.swap-marketplace__artifact-email input { border: 1px solid rgba(196, 220, 255, 0.18); border-radius: 14px; background: rgba(5, 10, 18, 0.74); color: #fff; padding: 10px 12px; }
.swap-marketplace__artifact-list { display: grid; gap: 10px; }
.swap-marketplace__artifact-list article { display: grid; gap: 5px; padding: 12px; border-radius: 18px; background: rgba(255,255,255,0.065); border: 1px solid rgba(196, 220, 255, 0.13); }
.swap-marketplace__artifact-list small { color: rgba(167, 243, 208, 0.78); font-size: 0.66rem; font-weight: 900; letter-spacing: 0.12em; text-transform: uppercase; }
.swap-marketplace__artifact-list strong { color: #fff; }
.swap-marketplace__artifact-list span { color: rgba(226, 238, 250, 0.76); font-size: 0.86rem; line-height: 1.42; overflow-wrap: anywhere; }
.swap-marketplace__artifact-list a { color: #95f5d0; font-weight: 900; overflow-wrap: anywhere; }
.swap-marketplace__messages-panel { display: grid; gap: 12px; margin-top: 14px; padding: 14px; border-radius: 20px; border: 1px solid rgba(109, 168, 255, 0.22); background: rgba(2, 8, 23, 0.42); }
.swap-marketplace__messages-head { display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; }
.swap-marketplace__messages-head span { color: rgba(167, 243, 208, 0.86); font-size: 0.7rem; font-weight: 900; letter-spacing: 0.14em; text-transform: uppercase; }
.swap-marketplace__messages-head p { margin: 0; color: rgba(226, 238, 250, 0.72); }
.swap-marketplace__thread-list { display: grid; gap: 12px; }
.swap-marketplace__thread { display: grid; gap: 10px; padding: 12px; border-radius: 18px; background: rgba(255,255,255,0.055); border: 1px solid rgba(196, 220, 255, 0.12); }
.swap-marketplace__thread-title { display: flex; justify-content: space-between; gap: 10px; color: rgba(226, 238, 250, 0.72); }
.swap-marketplace__thread-title strong { color: #fff; }
.swap-marketplace__message-list { display: grid; gap: 7px; }
.swap-marketplace__message { display: grid; gap: 3px; margin: 0; padding: 9px 10px; border-radius: 14px; color: rgba(226, 238, 250, 0.78); background: rgba(255,255,255,0.055); }
.swap-marketplace__message small { color: rgba(167, 243, 208, 0.76); font-size: 0.64rem; font-weight: 900; text-transform: uppercase; letter-spacing: 0.12em; }
.swap-marketplace__message span { font-size: 0.86rem; line-height: 1.42; }
.swap-marketplace__message.is-system { border: 1px solid rgba(120, 240, 202, 0.16); }
.swap-marketplace__message.is-buyer { background: rgba(109, 168, 255, 0.12); }
.swap-marketplace__message.is-admin { background: rgba(120, 240, 202, 0.1); }
.swap-marketplace__message-compose { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 10px; align-items: end; }
.swap-marketplace__message-compose textarea { border: 1px solid rgba(196, 220, 255, 0.18); border-radius: 14px; background: rgba(5, 10, 18, 0.74); color: #fff; padding: 10px 12px; resize: vertical; }
.swap-marketplace__dispute-panel { display: grid; gap: 12px; margin-top: 14px; padding: 14px; border-radius: 20px; border: 1px solid rgba(251, 191, 36, 0.28); background: linear-gradient(135deg, rgba(251, 191, 36, 0.12), rgba(239, 68, 68, 0.08)); }
.swap-marketplace__dispute-panel > div:first-child { display: grid; gap: 4px; }
.swap-marketplace__dispute-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
.swap-marketplace__dispute-grid label { display: grid; gap: 5px; color: rgba(226, 238, 250, 0.78); font-size: 0.78rem; font-weight: 800; }
.swap-marketplace__dispute-grid input, .swap-marketplace__dispute-grid select, .swap-marketplace__dispute-grid textarea { border: 1px solid rgba(196, 220, 255, 0.18); border-radius: 14px; background: rgba(5, 10, 18, 0.74); color: #fff; padding: 9px 10px; }
.swap-marketplace__dispute-grid textarea { resize: vertical; min-height: 84px; }
.swap-marketplace__stripe-box { display: grid; gap: 12px; margin-top: 14px; padding: 14px; border-radius: 20px; border: 1px solid rgba(120, 240, 202, 0.2); background: rgba(2, 8, 23, 0.42); }
@media (max-width: 980px) { .swap-marketplace__timeline { grid-template-columns: 1fr; } }
@media (max-width: 760px) { .swap-marketplace__header, .swap-marketplace__price-row, .swap-marketplace__quote-bar, .swap-marketplace__cart, .swap-marketplace__lookup, .swap-marketplace__messages-head, .swap-marketplace__message-compose, .swap-marketplace__results-head, .swap-marketplace__search-panel, .swap-details-guard { display: grid; grid-template-columns: 1fr; } .swap-marketplace__steps, .swap-marketplace__locality-grid, .swap-marketplace__checkout-form, .swap-marketplace__dispute-grid { grid-template-columns: 1fr; } .swap-marketplace__hold-button, .swap-marketplace__cart-actions button, .swap-marketplace__lookup button, .swap-marketplace__messages-head button, .swap-marketplace__select-result { width: 100%; } .swap-marketplace__together-check { white-space: normal; } }
</style>
